import { GoldStrategyInputsPanel } from './goldStrategyOperations';
import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../shared/api';
import { MaskedName } from '../shared/nameMask';
import { Breadcrumb } from '../shared/Breadcrumb';
import { readUrlParam, useUrlSync, CopyLinkButton } from '../shared/urlState';
import { AnnotationStrip } from './adoModule';
import { ORDER_STATUS, ORDER_STATUS_SEQUENCE, ORDER_STATUS_TYPES, carrierLabel as orderCarrierLabel, currentOrderStatus, orderStatusLabel, orderStatusTooltip } from '../shared/orderStatus';
import { OpenOrdersAgingPanel } from './openOrdersAgingPanel';

type Locale = 'hr' | 'en';

type Inventory = { id: number; code: string; name_hr: string; name_en: string; sort_order: number; visible?: boolean };
type Product = { id: number; sku: string | null; name_hr: string; name_en: string | null };
type Level = { inventory_id: number; product_id: number; total_quantity: number; reserved_quantity: number; available_quantity: number; unit: string | null };
type Department = { id: number; company_id: number; code: string | null; name: string };
type Reservation = {
    id: number; reservation_order_id: string | null; reservation_order_public_code: string | null;
    inventory_id: number; product_id: number; branch_department_id: number;
    quantity: number; status: string; requested_by: number;
    reserved_at: string; expires_at: string | null;
    fulfilled_at: string | null; fulfilled_by: number | null; fulfilled_quantity: number | null;
    erp_reference: string | null; json_orphan?: boolean | number | null; cancelled_at: string | null; cancelled_by: number | null; cancel_reason: string | null;
    notes: string | null;
    product_sku: string | null; product_name_hr: string;
    inventory_code: string; inventory_name_hr: string;
    department_code: string | null; department_name: string;
    requested_by_name: string | null;
    fulfilled_by_name: string | null;
    cancelled_by_name: string | null;
};

type OwnStockRow = { sku: string | null; name_hr: string; name_en: string | null; total_quantity: number; in_transit_quantity?: number; reserved_quantity: number; available_quantity: number };

type Overview = {
    inventories: Inventory[];
    own_warehouse_code: string | null;
    own_stock: OwnStockRow[];
    own_snapshot_at: string | null;
    products: Product[];
    levels: Level[];
    departments: Department[];
    settings: { auto_expiry_days: number };
    is_manager: boolean;
    is_owner?: boolean;
    notify_stock_reservation: boolean;
    primary_department_id: number | null;
    primary_department_name: string | null;
};

type CartItem = { product_id: number; product_name: string; sku: string | null; unit: string | null; available: number; qty: number };

type TransitItem = { order_number: string; partner_name: string | null; item_name: string | null; qty: number; eta_date: string | null; layer: string; from: string; from_name: string; to: string; to_name: string };
type TransitGroup = { code: string; name: string; items: TransitItem[] };
type ContributingOrder = { order_number: string; qty: number };
type TransitMismatch = { item_code: string; item_name: string | null; warehouse: string; warehouse_name: string; status: string; committed: number; available: number; shortfall: number; orders: number; contributing_orders?: ContributingOrder[] };
type ArticleChange = { order_number: string; bis_items_missing: Array<{ bis_item: string; bis_qty: number }>; faros_items_current: Array<{ item_name: string; qty: number }> };

type AutoClosedRow = {
    id: number;
    order_number: string;
    partner_name: string | null;
    item_code: string | null;
    item_name: string | null;
    qty: number;
    source_warehouse_code: string | null;
    source_warehouse_name: string | null;
    dest_branch_code: string | null;
    dest_branch_name: string | null;
    reason: string;
    delivered_at: string | null;
};

/**
 * Banner za auto-zatvorene IMAM JU alokacije (poluga isporučena drugome iz istog skladišta).
 * Korisnik = poslovnica (filter na backendu po source/dest match-u s primary_department.json_pos_id).
 * "Razumio sam" → ack svih ili pojedinačno.
 */
function AutoClosedBanner({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<AutoClosedRow[]>([]);
    const [busy, setBusy] = useState<number | 'all' | ''>('');
    const reload = useCallback(() => api<{ rows: AutoClosedRow[] }>('/warehouse-stock/auto-closed', token).then((r) => setRows(r.rows ?? [])).catch(() => undefined), [token]);
    useEffect(() => { void reload(); }, [reload]);
    const ack = async (ids: number[], key: number | 'all') => {
        setBusy(key);
        try {
            await mutate('/warehouse-stock/auto-closed/ack', token, 'POST', { ids });
            await reload();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(''); }
    };
    if (rows.length === 0) return null;
    const fmtTd = (s: string | null) => { if (!s) return '—'; const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s)); return m ? `${m[3]}.${m[2]}.${m[1]}.` : String(s); };
    return (
        <div style={{ background: '#fff4e0', border: '1px solid var(--ad-acc)', borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontWeight: 800, color: 'var(--ad-acc-3)', fontSize: '0.92rem' }}>
                    ⚠ {t('Auto-zatvorene SMJEŠTENO oznake — poluga isporučena drugome', 'Auto-closed PLACED flags — bar delivered to someone else')} ({rows.length})
                </div>
                <button type="button" disabled={busy !== ''} onClick={() => void ack(rows.map((r) => r.id), 'all')}
                    style={{ background: '#1f7a36', color: '#fff', border: 0, borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700 }}>
                    ✓ {t('Razumio sam sve', 'Acknowledge all')}
                </button>
            </div>
            <div style={{ fontSize: '0.74rem', color: '#7a5a14', marginBottom: 8 }}>
                {t('Sustav je detektirao da je poluga koju ste prethodno označili "Smješteno" nestala s vašeg skladišta (vjerojatno prodana drugom kupcu). Oznaka je automatski zatvorena, narudžbu je potrebno preusmjeriti na drugi izvor.', 'The system detected the bar you had marked "Placed" is no longer on stock (likely sold to a walk-in customer). The flag was auto-closed; the order needs re-routing to another source.')}
            </div>
            <table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ color: 'var(--ad-ink)', textAlign: 'left' }}>
                        <th style={{ padding: '2px 4px' }}>{t('Narudžba', 'Order')}</th>
                        <th style={{ padding: '2px 4px' }}>{t('Kupac', 'Customer')}</th>
                        <th style={{ padding: '2px 4px' }}>{t('Artikl', 'Item')}</th>
                        <th style={{ textAlign: 'right', padding: '2px 4px' }}>{t('Kol.', 'Qty')}</th>
                        <th style={{ padding: '2px 4px' }}>{t('Iz skladišta', 'From')}</th>
                        <th style={{ padding: '2px 4px' }}>{t('Za poslovnicu', 'To branch')}</th>
                        <th style={{ padding: '2px 4px' }}>{t('Zatvoreno', 'Closed')}</th>
                        <th style={{ padding: '2px 4px' }}></th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} style={{ borderTop: '1px solid rgba(var(--ad-soft-rgb),0.3)' }}>
                            <td style={{ padding: '3px 4px', fontFamily: 'ui-monospace, monospace' }}>{r.order_number}</td>
                            <td style={{ padding: '3px 4px' }}><MaskedName name={r.partner_name} /></td>
                            <td style={{ padding: '3px 4px' }}>{r.item_name ?? r.item_code ?? '—'}</td>
                            <td style={{ textAlign: 'right', padding: '3px 4px' }}>{r.qty}</td>
                            <td style={{ padding: '3px 4px', color: 'var(--ad-ink)' }}>{r.source_warehouse_code ?? '—'} · {r.source_warehouse_name ?? ''}</td>
                            <td style={{ padding: '3px 4px' }}>{r.dest_branch_code ?? '—'} · {r.dest_branch_name ?? ''}</td>
                            <td style={{ padding: '3px 4px', color: '#777' }}>{fmtTd(r.delivered_at)}</td>
                            <td style={{ padding: '3px 4px', textAlign: 'right' }}>
                                <button type="button" disabled={busy !== ''} onClick={() => void ack([r.id], r.id)}
                                    style={{ background: 'transparent', border: '1px solid #1f7a36', color: '#1f7a36', borderRadius: 4, padding: '1px 8px', cursor: 'pointer', fontSize: '0.72rem' }}>
                                    ✓ {t('Razumio sam', 'Got it')}
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/** Roba u tranzitu (iz plana isporuka 40.4, status NA PUTU) — u dolasku po poslovnici, u odlasku po skladištu. */
function TransitOverview({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const fmtTd = (s: string | null) => { if (!s) return '—'; const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s)); return m ? `${m[3]}.${m[2]}.${m[1]}.` : String(s); };
    const [data, setData] = useState<{ incoming: TransitGroup[]; outgoing: TransitGroup[]; mismatches: TransitMismatch[]; article_changes?: ArticleChange[] } | null>(null);
    const [open, setOpen] = useState(false);
    const [expandedMism, setExpandedMism] = useState<number | null>(null);
    const [closing, setClosing] = useState<string>(''); // running action key
    const reload = useCallback(() => api<{ incoming: TransitGroup[]; outgoing: TransitGroup[]; mismatches: TransitMismatch[]; article_changes?: ArticleChange[] }>('/warehouse-stock/transit', token).then(setData).catch(() => undefined), [token]);
    useEffect(() => { void reload(); }, [token]);
    const inN = (data?.incoming ?? []).reduce((s, g) => s + g.items.length, 0);
    const outN = (data?.outgoing ?? []).reduce((s, g) => s + g.items.length, 0);
    const mism = data?.mismatches ?? [];
    const articleChanges = data?.article_changes ?? [];
    if (!data || (inN === 0 && outN === 0 && mism.length === 0 && articleChanges.length === 0)) return null;
    const closeMismatch = async (m: TransitMismatch, orderNumbers?: string[]) => {
        const key = `${m.item_code}|${m.warehouse}|${m.status}|${(orderNumbers ?? []).join(',')}`;
        setClosing(key);
        try {
            await mutate('/warehouse-stock/usklada/close', token, 'POST', {
                item_code: m.item_code,
                warehouse_code: m.warehouse,
                status: m.status,
                order_numbers: orderNumbers,
            });
            await reload();
        } catch (e) {
            alert(e instanceof Error ? e.message : 'API');
        } finally { setClosing(''); }
    };
    const GOLD = 'var(--ad-acc)'; const BROWN = 'var(--ad-ink)';
    const groupBlock = (groups: TransitGroup[], dir: 'in' | 'out') => (
        <div style={{ display: 'grid', gap: 6 }}>
            {groups.map((g) => (
                <div key={g.code} style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.25)', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontWeight: 700, color: BROWN, fontSize: '0.84rem', marginBottom: 4 }}>{dir === 'in' ? '📥' : '📤'} {g.code} · {g.name} <span className="muted" style={{ fontWeight: 400 }}>({g.items.length})</span></div>
                    <table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse' }}>
                        <tbody>
                            {g.items.map((it, i) => (
                                <tr key={i} style={{ borderTop: '1px solid #f0ead7' }}>
                                    <td style={{ fontFamily: 'ui-monospace, monospace', padding: '2px 4px' }}>{it.order_number}</td>
                                    <td style={{ padding: '2px 4px' }}>{it.item_name}</td>
                                    <td style={{ textAlign: 'right', padding: '2px 4px' }}>{it.qty} kom</td>
                                    <td style={{ padding: '2px 4px', color: '#777' }}>{dir === 'in' ? `← ${it.from}` : `→ ${it.to}`}</td>
                                    <td style={{ padding: '2px 4px', fontWeight: 700, color: BROWN }}>ETA {fmtTd(it.eta_date)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
        </div>
    );
    return (
        <div style={{ border: `1px solid ${mism.length > 0 ? '#b14040' : GOLD}`, borderRadius: 12, padding: 12, marginBottom: 14, background: mism.length > 0 ? 'rgba(177,64,64,0.05)' : 'rgba(var(--ad-acc-rgb),0.05)' }}>
            {mism.length > 0 ? (
                <div style={{ background: '#fde8e8', border: '1px solid #b14040', borderRadius: 8, padding: '8px 11px', marginBottom: 10 }}>
                    <div style={{ fontWeight: 800, color: '#b14040', fontSize: '0.86rem', marginBottom: 6 }}>⚠ {t('Usklada — potvrđeno više nego na zalihi', 'Reconcile — committed exceeds stock')} ({mism.length})</div>
                    <table style={{ width: '100%', fontSize: '0.76rem', borderCollapse: 'collapse' }}>
                        <thead><tr style={{ color: '#8a3a3a', textAlign: 'left' }}><th style={{ padding: '2px 4px' }}>{t('Artikl', 'Item')}</th><th style={{ padding: '2px 4px' }}>{t('Skladište', 'WH')}</th><th style={{ padding: '2px 4px' }}>Status</th><th style={{ textAlign: 'right', padding: '2px 4px' }}>{t('Potvrđeno', 'Committed')}</th><th style={{ textAlign: 'right', padding: '2px 4px' }}>{t('Zaliha', 'Stock')}</th><th style={{ textAlign: 'right', padding: '2px 4px' }}>{t('Manjak', 'Short')}</th><th style={{ textAlign: 'right', padding: '2px 4px' }}>{t('Nar.', 'Ord.')}</th><th style={{ padding: '2px 4px' }}></th></tr></thead>
                        <tbody>
                            {mism.map((m, i) => (
                                <React.Fragment key={i}>
                                    <tr style={{ borderTop: '1px solid rgba(177,64,64,0.2)' }}>
                                        <td style={{ padding: '2px 4px' }}>{m.item_name ?? m.item_code}</td>
                                        <td style={{ padding: '2px 4px' }}>{m.warehouse} · {m.warehouse_name}</td>
                                        <td style={{ padding: '2px 4px' }}>{m.status === 'imam_ju' ? 'SMJEŠTENO' : 'POSLANO'}</td>
                                        <td style={{ textAlign: 'right', padding: '2px 4px' }}>{m.committed}</td>
                                        <td style={{ textAlign: 'right', padding: '2px 4px' }}>{m.available}</td>
                                        <td style={{ textAlign: 'right', padding: '2px 4px', fontWeight: 800, color: '#b14040' }}>−{m.shortfall}</td>
                                        <td style={{ textAlign: 'right', padding: '2px 4px' }}>{m.orders}</td>
                                        <td style={{ padding: '2px 4px', whiteSpace: 'nowrap' }}>
                                            <button type="button" onClick={() => setExpandedMism(expandedMism === i ? null : i)} style={{ background: 'transparent', border: '1px solid #b14040', color: '#b14040', borderRadius: 4, padding: '1px 6px', cursor: 'pointer', fontSize: '0.7rem', marginRight: 4 }}>
                                                {expandedMism === i ? '▾' : '▸'} {t('NR', 'NR')}
                                            </button>
                                            <button type="button" disabled={!!closing} onClick={() => void closeMismatch(m)} style={{ background: '#1f7a36', color: '#fff', border: 0, borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700 }}>
                                                ✓ {t('Zatvori sve', 'Close all')}
                                            </button>
                                        </td>
                                    </tr>
                                    {expandedMism === i && (m.contributing_orders ?? []).length > 0 ? (
                                        <tr><td colSpan={8} style={{ padding: '4px 8px', background: 'rgba(177,64,64,0.04)' }}>
                                            <div style={{ fontSize: '0.7rem', color: '#8a3a3a', marginBottom: 3 }}>{t('Narudžbe koje doprinose ovom manjku:', 'Orders contributing to this shortfall:')}</div>
                                            {(m.contributing_orders ?? []).map((co) => (
                                                <div key={co.order_number} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
                                                    <span style={{ fontFamily: 'ui-monospace, monospace', minWidth: 200 }}>{co.order_number}</span>
                                                    <span style={{ fontSize: '0.74rem', color: '#666' }}>{co.qty} kom</span>
                                                    <button type="button" disabled={!!closing} onClick={() => void closeMismatch(m, [co.order_number])} style={{ background: 'transparent', border: '1px solid #1f7a36', color: '#1f7a36', borderRadius: 4, padding: '1px 6px', cursor: 'pointer', fontSize: '0.7rem', marginLeft: 'auto' }}>
                                                        ✓ {t('Zatvori', 'Close')}
                                                    </button>
                                                </div>
                                            ))}
                                        </td></tr>
                                    ) : null}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                    <div style={{ fontSize: '0.7rem', color: '#8a3a3a', marginTop: 4 }}>{t('Potvrđena roba nema pokriće na zalihi (npr. Faros već fakturirao). Klikni "Zatvori" da označiš kao isporučenu (undo preko prev_status).', 'Committed goods lack stock cover (e.g. Faros already invoiced). Click "Close" to mark as delivered (undo via prev_status).')}</div>
                </div>
            ) : null}
            {articleChanges.length > 0 ? (
                <div style={{ background: '#fff8e7', border: '1px solid var(--ad-acc)', borderRadius: 8, padding: '8px 11px', marginBottom: 10 }}>
                    <div style={{ fontWeight: 800, color: 'var(--ad-acc-3)', fontSize: '0.86rem', marginBottom: 6 }}>🔄 {t('Artikl zamijenjen u Farosu — provjeri', 'Article swapped in Faros — review')} ({articleChanges.length})</div>
                    <table style={{ width: '100%', fontSize: '0.76rem', borderCollapse: 'collapse' }}>
                        <thead><tr style={{ color: 'var(--ad-ink)', textAlign: 'left' }}>
                            <th style={{ padding: '2px 4px' }}>{t('Narudžba', 'Order')}</th>
                            <th style={{ padding: '2px 4px' }}>{t('Bilo u BIS-u (imam_ju/u_dolasku)', 'In BIS')}</th>
                            <th style={{ padding: '2px 4px' }}>{t('Sada u Farosu', 'Now in Faros')}</th>
                        </tr></thead>
                        <tbody>
                            {articleChanges.map((ac, i) => (
                                <tr key={i} style={{ borderTop: '1px solid rgba(var(--ad-soft-rgb),0.3)' }}>
                                    <td style={{ padding: '4px', fontFamily: 'ui-monospace, monospace' }}>{ac.order_number}</td>
                                    <td style={{ padding: '4px' }}>
                                        {ac.bis_items_missing.map((bi, k) => (
                                            <div key={k} style={{ color: '#b14040' }}>− {bi.bis_qty}× {bi.bis_item}</div>
                                        ))}
                                    </td>
                                    <td style={{ padding: '4px' }}>
                                        {ac.faros_items_current.map((fi, k) => (
                                            <div key={k} style={{ color: '#1f7a36' }}>+ {fi.qty}× {fi.item_name}</div>
                                        ))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div style={{ fontSize: '0.7rem', color: 'var(--ad-acc-3)', marginTop: 4 }}>
                        {t('Faros je promijenio artikl na istoj NR (dogovor s klijentom / zamjena artikla). Stara BIS oznaka je još aktivna ali ne odgovara novom Faros stavku — zatvori je kroz Uskladu gore (allociranje će se ponovo izračunati u sljedećem planu).', 'Faros changed the article on the same NR. The old BIS flag is still active but no longer matches the new Faros item — close it via Reconcile above (allocation will be recomputed in the next plan).')}
                    </div>
                </div>
            ) : null}
            <button type="button" onClick={() => setOpen((o) => !o)} style={{ background: 'none', border: 0, cursor: 'pointer', fontWeight: 800, color: BROWN, fontSize: '0.95rem', padding: 0 }}>
                {open ? '▾' : '▸'} 🚚 {t('Roba u tranzitu (po narudžbama)', 'Goods in transit (by order)')} — 📥 {inN} {t('u dolasku', 'incoming')} · 📤 {outN} {t('u odlasku', 'outgoing')}
            </button>
            {open ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, marginTop: 10 }}>
                    <div>
                        <div style={{ fontWeight: 800, color: '#1f7a36', fontSize: '0.8rem', marginBottom: 6 }}>📥 {t('U DOLASKU (po poslovnici)', 'INCOMING (by branch)')}</div>
                        {inN > 0 ? groupBlock(data.incoming, 'in') : <span className="muted" style={{ fontSize: '0.8rem' }}>—</span>}
                    </div>
                    <div>
                        <div style={{ fontWeight: 800, color: '#1e40af', fontSize: '0.8rem', marginBottom: 6 }}>📤 {t('U ODLASKU (po skladištu)', 'OUTGOING (by warehouse)')}</div>
                        {outN > 0 ? groupBlock(data.outgoing, 'out') : <span className="muted" style={{ fontSize: '0.8rem' }}>—</span>}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export function WarehouseStockModule({ locale, token, onBack }: { locale: Locale; token: string; onBack?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [overview, setOverview] = useState<Overview | null>(null);
    const [reservations, setReservations] = useState<Reservation[]>([]);
    // Default: Svoje (fizičko stanje poslovnice). Inicijalno null pa nakon load-a set.
    const [activeInv, setActiveInv] = useState<number | 'svoje' | null>(null);
    const [tab, setTab] = useState<'stock' | 'reservations' | 'manual_alloc' | 'open_orders_aging' | 'settings' | 'strategy'>(() => {
        const t = readUrlParam('tab', 'stock');
        return ['stock', 'reservations', 'manual_alloc', 'open_orders_aging', 'settings', 'strategy'].includes(t) ? t as any : 'stock';
    });
    useUrlSync('tab', tab, tab === 'stock');
    const [cart, setCart] = useState<CartItem[]>([]);
    const [submittingCart, setSubmittingCart] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [emailResult, setEmailResult] = useState<{ ok: boolean; output: string } | null>(null);
    const [resForm, setResForm] = useState<{ open: boolean; product_id: number | null; product_name: string; available: number; unit: string | null; qty: string; dept: number | null; notes: string }>({ open: false, product_id: null, product_name: '', available: 0, unit: null, qty: '', dept: null, notes: '' });
    const [stockForm, setStockForm] = useState<{ open: boolean; product_id: number | null; product_sku: string; product_name: string; kind: 'initial' | 'adjustment'; delta: string; notes: string }>({ open: false, product_id: null, product_sku: '', product_name: '', kind: 'initial', delta: '', notes: '' });
    const [fulfillForm, setFulfillForm] = useState<{ open: boolean; reservation: Reservation | null; quantity: string; erpRef: string; notes: string }>({ open: false, reservation: null, quantity: '', erpRef: '', notes: '' });

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [ov, rs] = await Promise.all([
                api<Overview>('/warehouse-stock/overview', token),
                api<{ rows: Reservation[] }>('/warehouse-stock/reservations', token),
            ]);
            setOverview(ov);
            setReservations(rs.rows);
            if (activeInv == null) {
                // Default: "Svoje" (fizičko stanje poslovnice iz FAROS-a) ako poslovnica ima svoj warehouse.
                // Inače Komisijsko (code='komisijsko'), inače prvo VIDLJIVO skladište.
                const shown = ov.inventories.filter((i) => i.visible !== false);
                if (ov.own_warehouse_code) {
                    setActiveInv('svoje');
                } else if (shown.length > 0) {
                    const komisijsko = shown.find((i) => i.code === 'komisijsko') ?? shown[0];
                    setActiveInv(Number(komisijsko.id));
                }
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Učitavanje nije uspjelo.');
        } finally {
            setLoading(false);
        }
    }, [token, activeInv]);

    useEffect(() => { void reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (! overview) {
        return (
            <section className="card admin-block">
                <h3>{t('40.3 · Skladište', '40.3 · Inventory')}</h3>
                {error ? <p className="error">{error}</p> : <p className="muted">{t('Učitavam…', 'Loading…')}</p>}
            </section>
        );
    }

    const isManager = overview.is_manager;
    // Vlasnik (vjakomin@gmail.com) jedini vidi checkboxe i skrivena skladišta.
    const isOwner = overview.is_owner === true;

    /** Pali/gasi prikaz skladišta za CIJELI BIS (podaci ostaju netaknuti). */
    const toggleInvVisibility = async (code: string, visible: boolean): Promise<void> => {
        try {
            await mutate('/warehouse-stock/inventory-visibility', token, 'PATCH', { code, visible });
            const inv = overview.inventories.find((i) => i.code === code);
            if (! visible && inv && Number(activeInv) === Number(inv.id)) {
                // Gasimo skladište koje se trenutno gleda — prebaci na Svoje ili prvo preostalo vidljivo.
                const next = overview.inventories.find((i) => Number(i.id) !== Number(inv.id) && i.visible !== false);
                setActiveInv(overview.own_warehouse_code ? 'svoje' : next ? Number(next.id) : null);
            }
            await reload();
        } catch (e) {
            setError(e instanceof Error ? e.message : t('Promjena vidljivosti nije uspjela.', 'Visibility change failed.'));
        }
    };

    const fmtSnapshot = (s: string | null): string => {
        if (! s) return '';
        const m = String(s).match(/(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
        if (! m) return String(s);
        const [, y, mo, d, hh, mi] = m;
        return hh ? `${d}.${mo}.${y}. ${hh}:${mi}` : `${d}.${mo}.${y}.`;
    };
    const fmtQty = (n: number | string | null) => {
        if (n == null) return '—';
        const num = Number(n);
        if (! Number.isFinite(num)) return '—';
        return Number.isInteger(num) ? String(num) : num.toFixed(2).replace('.', ',');
    };
    const productMap: Record<number, Product> = Object.fromEntries(overview.products.map((p) => [p.id, p]));
    // #3 Ružica/T87 (mail UID 663, 18.09.2026): "sakrij Rezervacije u 40.3 AKO se odnose na
    // komisiju" — uvjetno, samo komisijske retke, ne cijeli tab i ne akcijske rezervacije.
    const visibleReservations = reservations.filter((r) => r.inventory_code !== 'komisijsko');

    const showOwn = activeInv === 'svoje';
    const activeLevels = overview.levels.filter((l) => Number(l.inventory_id) === Number(activeInv));
    const levelMap = Object.fromEntries(activeLevels.map((l) => [l.product_id, l]));
    const visibleProducts = overview.products.filter((p) => {
        if (! search.trim()) return true;
        const q = search.toLowerCase();
        return (p.sku ?? '').toLowerCase().includes(q) || p.name_hr.toLowerCase().includes(q) || (p.name_en ?? '').toLowerCase().includes(q);
    });
    const visibleOwn = (overview.own_stock ?? []).filter((r) => {
        if (! search.trim()) return true;
        const q = search.toLowerCase();
        return (r.sku ?? '').toLowerCase().includes(q) || r.name_hr.toLowerCase().includes(q) || (r.name_en ?? '').toLowerCase().includes(q);
    });

    return (
        <section className="card admin-block">
            <Breadcrumb items={[
                { label: t('🏭 40 · Skladišno poslovanje', '🏭 40 · Warehouse'), onClick: onBack },
                { label: t('40.3 · Skladište', '40.3 · Inventory') },
            ]} />
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">📦</div>
                <div className="bi-module-hero-text">
                    <h1>{t('40.3 · Skladište', '40.3 · Inventory')}</h1>
                    <p className="muted">{t('Paralelno praćenje akcijskog i komisijskog skladišta — radi sve dok ne stigne FAROS.', 'Parallel tracking of promotional and consignment stock until FAROS arrives.')}</p>
                </div>
                <CopyLinkButton locale={locale} />
            </div>

            <AutoClosedBanner token={token} locale={locale} />
            <TransitOverview token={token} locale={locale} />

            {/* Email fetch result banner — galantna obavijest, isto kao 40.2 */}
            {emailResult ? (
                <div style={{
                    padding: '0.7rem 0.9rem',
                    marginBottom: '1rem',
                    borderRadius: 10,
                    background: emailResult.ok ? 'rgba(31,122,54,0.08)' : 'rgba(177,64,64,0.08)',
                    border: emailResult.ok ? '1px solid rgba(31,122,54,0.25)' : '1px solid rgba(177,64,64,0.25)',
                    fontSize: '0.85rem',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <strong style={{ color: emailResult.ok ? '#1f7a36' : '#b14040' }}>
                            {emailResult.ok ? '✓' : '✗'} {emailResult.ok ? t('Email fetch dovršen', 'Email fetch complete') : t('Email fetch greška', 'Email fetch failed')}
                        </strong>
                        <button type="button" onClick={() => setEmailResult(null)} style={{ marginLeft: 'auto', background: 'transparent', border: 0, cursor: 'pointer', color: 'rgba(0,0,0,0.5)', fontSize: '1.1rem', fontWeight: 700 }}>×</button>
                    </div>
                    {emailResult.output ? (
                        <pre style={{ margin: '6px 0 0', fontSize: '0.74rem', color: 'rgba(0,0,0,0.7)', maxHeight: 140, overflowY: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{emailResult.output.trim()}</pre>
                    ) : null}
                </div>
            ) : null}

            {error ? <p className="error">{error}</p> : null}

            {/* Top tabs (Stock | Reservations | Settings) */}
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem', borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.2)', paddingBottom: 6 }}>
                {[
                    { key: 'stock' as const, label: t('Stanje', 'Stock') },
                    { key: 'reservations' as const, label: t('Rezervacije', 'Reservations') + ` (${visibleReservations.filter((r) => r.status === 'pending').length})` },
                    { key: 'manual_alloc' as const, label: '🔒 ' + t('Ručna alokacija narudžbi', 'Manual order allocation') },
                    { key: 'open_orders_aging' as const, label: t('Otvorene narudžbe po starosti', 'Open orders by age') },
                    ...(isOwner ? [{ key: 'strategy' as const, label: t('Ulazi strategije', 'Strategy inputs') }] : []),
                    ...(isManager ? [{ key: 'settings' as const, label: t('Postavke', 'Settings') }] : []),
                ].map((x) => (
                    <button key={x.key} type="button" onClick={() => setTab(x.key)} style={{
                        background: tab === x.key ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : 'transparent',
                        color: tab === x.key ? '#fffbeb' : 'var(--ad-ink)',
                        border: tab === x.key ? 0 : '1px solid rgba(var(--ad-soft-rgb),0.4)',
                        padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.86rem',
                    }}>{x.label}</button>
                ))}
            </div>

            {tab === 'stock' ? (
                <>
                    {/* Prominent inventory selector — skriven kad nema ničega za birati
                        (npr. skladišta ugašena, a poslovnica nema svoj FAROS warehouse). */}
                    <div style={{
                        display: overview.own_warehouse_code || overview.inventories.length > 0 ? 'flex' : 'none',
                        gap: '0.6rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center',
                        padding: '0.8rem 1rem', borderRadius: 14,
                        background: 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.5) 100%)',
                        border: '1.5px solid rgba(var(--ad-soft-rgb),0.35)',
                    }}>
                        <strong style={{ color: 'var(--ad-ink)', fontSize: '0.86rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>📦 {t('Skladište', 'Inventory')}</strong>
                        {overview.own_warehouse_code ? (
                            <button type="button" onClick={() => setActiveInv('svoje')} style={{
                                background: activeInv === 'svoje' ? 'linear-gradient(135deg, #1f7a36, #15803d)' : '#fff',
                                color: activeInv === 'svoje' ? '#f0fdf4' : '#1f7a36',
                                border: activeInv === 'svoje' ? 0 : '1.5px solid rgba(31,122,54,0.4)',
                                padding: '10px 22px', borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: '1rem',
                                boxShadow: activeInv === 'svoje' ? '0 4px 12px rgba(21,128,61,0.28)' : '0 1px 3px rgba(0,0,0,0.05)',
                                transition: 'all 140ms ease',
                            }}>🏪 {t('Svoje', 'Own')}</button>
                        ) : null}
                        {overview.inventories.map((inv) => {
                            const active = Number(activeInv) === Number(inv.id);
                            const hidden = inv.visible === false;  // samo vlasnik dobiva skrivena skladišta u odgovoru
                            return (
                                <span key={inv.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    {isOwner ? (
                                        <input
                                            type="checkbox"
                                            checked={! hidden}
                                            onChange={(e) => { void toggleInvVisibility(inv.code, e.target.checked); }}
                                            title={t('Prikaži ovo skladište svima u BIS-u', 'Show this inventory to everyone in BIS')}
                                            style={{ width: 16, height: 16, cursor: 'pointer' }}
                                        />
                                    ) : null}
                                    <button type="button" onClick={() => setActiveInv(Number(inv.id))} disabled={hidden} style={{
                                        background: active ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : '#fff',
                                        color: active ? '#fffbeb' : 'var(--ad-ink)',
                                        border: active ? 0 : hidden ? '1.5px dashed rgba(0,0,0,0.25)' : '1.5px solid rgba(var(--ad-soft-rgb),0.4)',
                                        padding: '10px 22px', borderRadius: 12, cursor: hidden ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: '1rem',
                                        boxShadow: active ? '0 4px 12px rgba(107,79,28,0.28)' : '0 1px 3px rgba(0,0,0,0.05)',
                                        opacity: hidden ? 0.45 : 1,
                                        transition: 'all 140ms ease',
                                    }} title={hidden ? t('Skriveno svima — uključi checkboxom', 'Hidden for everyone — enable with the checkbox') : undefined}>
                                        {locale === 'en' ? inv.name_en : inv.name_hr}{hidden ? ' 🚫' : ''}
                                    </button>
                                </span>
                            );
                        })}
                        <span style={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.55)', marginLeft: 'auto' }}>
                            {isOwner
                                ? t('Checkbox = prikaz skladišta svima u BIS-u', 'Checkbox = show inventory to everyone in BIS')
                                : t('Klikni za promjenu prikaza', 'Click to switch view')}
                        </span>
                    </div>

                    {/* User context (your branch) */}
                    {overview.primary_department_id ? (
                        <p style={{ margin: '0 0 0.6rem', fontSize: '0.82rem', color: 'var(--ad-ink)' }}>
                            🏪 {t('Tvoja matična poslovnica', 'Your home branch')}: <strong>{overview.primary_department_name}</strong>
                            {showOwn && overview.own_warehouse_code ? (
                                <span style={{ color: 'rgba(0,0,0,0.55)' }}> · {t('fizičko stanje iz FAROS-a, skladište', 'physical FAROS stock, warehouse')} <strong>{overview.own_warehouse_code}</strong>
                                    {overview.own_snapshot_at ? <> · ⏱ {t('Učitano', 'Loaded')}: <strong>{fmtSnapshot(overview.own_snapshot_at)}</strong></> : null}
                                </span>
                            ) : null}
                        </p>
                    ) : (
                        <p style={{ margin: '0 0 0.6rem', fontSize: '0.82rem', color: '#b14040' }}>
                            ⚠ {t('Matična poslovnica nije postavljena (admin → 104 → Matična). Rezervacije neće raditi dok se ne postavi.', 'Home branch is not set (admin → 104). Reservations will not work.')}
                        </p>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: cart.length > 0 && ! showOwn ? '1fr 320px' : '1fr', gap: '1rem', alignItems: 'start' }}>
                        <div>
                            {/* Search + manager actions */}
                            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                <input
                                    type="search"
                                    placeholder={t('Pretraži šifru ili naziv…', 'Search SKU or name…')}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    style={{ flex: 1, minWidth: 200, padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(var(--ad-soft-rgb),0.35)', fontSize: '0.86rem' }}
                                />
                                {isManager ? (
                                    <>
                                        <button type="button" onClick={() => setStockForm({ open: true, product_id: null, product_sku: '', product_name: '', kind: 'initial', delta: '', notes: '' })} style={{
                                            background: 'linear-gradient(135deg, var(--ad-acc), #b8895a)', color: '#fff', border: 0,
                                            padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.86rem',
                                        }}>⚙ {t('Unesi / podesi stanje', 'Add / adjust stock')}</button>
                                        <button type="button" onClick={async () => {
                                            setEmailResult(null);
                                            try {
                                                const r = await api<{ ok: boolean; output: string }>('/warehouse-stock/fetch-email', token, { method: 'POST' });
                                                await reload();
                                                setEmailResult({ ok: r.ok, output: r.output ?? '' });
                                            } catch (e) {
                                                setEmailResult({ ok: false, output: e instanceof Error ? e.message : 'fail' });
                                            }
                                        }} className="secondary compact">📧 {t('Dohvati email', 'Fetch email')}</button>
                                    </>
                                ) : null}
                            </div>

                            <table className="admin-user-table">
                                <thead><tr>
                                    <th>{t('Šifra', 'SKU')}</th>
                                    <th>{t('Naziv', 'Name')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Ukupno', 'Total')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Rezervirano', 'Reserved')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Dostupno', 'Available')}</th>
                                    {showOwn ? <th style={{ textAlign: 'right' }} title={t('Roba na putu prema poslovnici, nije u stanju ni u dostupnom', 'Goods in transit to the branch, not in total or available')}>{t('Na putu', 'In transit')}</th> : <th />}
                                </tr></thead>
                                <tbody>
                                    {showOwn ? (
                                        visibleOwn.length === 0 ? (
                                            <tr><td colSpan={6} style={{ textAlign: 'center', padding: '1.4rem', color: 'rgba(0,0,0,0.45)' }}>{t('Nema fizičkog stanja za ovu poslovnicu (FAROS snapshot prazan).', 'No physical stock for this branch (FAROS snapshot empty).')}</td></tr>
                                        ) : visibleOwn.map((r) => (
                                            <tr key={`own-${r.sku}`}>
                                                <td><strong>{r.sku ?? '—'}</strong></td>
                                                <td>{locale === 'en' && r.name_en ? r.name_en : r.name_hr}</td>
                                                <td style={{ textAlign: 'right' }}>{fmtQty(r.total_quantity)} <span style={{ color: 'rgba(0,0,0,0.4)', fontSize: '0.75rem' }}>kom</span></td>
                                                <td style={{ textAlign: 'right', color: r.reserved_quantity > 0 ? 'var(--ad-acc)' : 'rgba(0,0,0,0.4)' }}>{fmtQty(r.reserved_quantity)}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 700, color: r.available_quantity > 0 ? '#1f7a36' : '#b14040' }}>{fmtQty(r.available_quantity)}</td>
                                                <td style={{ textAlign: 'right', color: (r.in_transit_quantity ?? 0) > 0 ? undefined : 'rgba(0,0,0,0.4)' }}>{fmtQty(r.in_transit_quantity ?? 0)}</td>
                                            </tr>
                                        ))
                                    ) : activeInv == null ? (
                                        <tr><td colSpan={6} style={{ textAlign: 'center', padding: '1.4rem', color: 'rgba(0,0,0,0.45)' }}>
                                            {t('Prikaz akcijskog i komisijskog skladišta je isključen — fizičko stanje prati FAROS.', 'Promotional & consignment stock display is switched off — physical stock is tracked in FAROS.')}
                                        </td></tr>
                                    ) : visibleProducts.filter((p) => {
                                        const lvl = levelMap[p.id];
                                        // Manager I user oboje vide samo proizvode koji imaju stanje ili rezervaciju.
                                        // Manager dobije sve aktivne proizvode tek kad otvori "Unesi/podesi stanje" modal.
                                        return (lvl?.total_quantity ?? 0) > 0 || (lvl?.reserved_quantity ?? 0) > 0;
                                    }).map((p) => {
                                        const lvl = levelMap[p.id];
                                        const total = lvl?.total_quantity ?? 0;
                                        const reserved = lvl?.reserved_quantity ?? 0;
                                        const available = lvl?.available_quantity ?? 0;
                                        const inCart = cart.find((c) => c.product_id === p.id);
                                        const remaining = available - (inCart?.qty ?? 0);
                                        return (
                                            <tr key={p.id}>
                                                <td><strong>{p.sku ?? '—'}</strong></td>
                                                <td>{locale === 'en' && p.name_en ? p.name_en : p.name_hr}</td>
                                                <td style={{ textAlign: 'right' }}>{fmtQty(total)} <span style={{ color: 'rgba(0,0,0,0.4)', fontSize: '0.75rem' }}>{lvl?.unit ?? 'kom'}</span></td>
                                                <td style={{ textAlign: 'right', color: reserved > 0 ? 'var(--ad-acc)' : 'rgba(0,0,0,0.4)' }}>{fmtQty(reserved)}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 700, color: available > 0 ? '#1f7a36' : '#b14040' }}>{fmtQty(available)}</td>
                                                <td style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                                    {remaining > 0 ? (
                                                        <button type="button" onClick={() => {
                                                            setCart((c) => {
                                                                const exists = c.find((x) => x.product_id === p.id);
                                                                if (exists) return c.map((x) => x.product_id === p.id ? { ...x, qty: x.qty + 1 } : x);
                                                                return [...c, { product_id: p.id, product_name: p.name_hr, sku: p.sku, unit: lvl?.unit ?? 'kom', available, qty: 1 }];
                                                            });
                                                        }} style={{
                                                            background: 'linear-gradient(135deg, #1f7a36, #15803d)', color: '#fff', border: 0,
                                                            padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '1rem',
                                                            lineHeight: 1,
                                                        }} title={t('Dodaj u košaricu', 'Add to cart')}>+</button>
                                                    ) : null}
                                                    {inCart ? <span style={{ fontSize: '0.78rem', color: '#1f7a36', fontWeight: 600 }}>🛒 {inCart.qty}</span> : null}
                                            {isManager ? (
                                                <button type="button" className="secondary compact" style={{ marginLeft: 4 }} onClick={() => setStockForm({
                                                    open: true,
                                                    product_id: p.id,
                                                    product_sku: p.sku ?? '',
                                                    product_name: p.name_hr,
                                                    kind: total === 0 ? 'initial' : 'adjustment',
                                                    delta: '',
                                                    notes: '',
                                                })}>⚙</button>
                                            ) : null}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                        </div>

                        {/* Cart sidebar (right) — skriven u "Svoje" pregledu (samo čitanje fizičkog stanja) */}
                        {cart.length > 0 && ! showOwn ? (
                            <aside style={{
                                position: 'sticky', top: 10,
                                background: 'linear-gradient(180deg, #fffbeb 0%, rgba(254,243,199,0.5) 100%)',
                                border: '1.5px solid rgba(var(--ad-soft-rgb),0.4)',
                                borderRadius: 14, padding: '0.9rem 1rem',
                                boxShadow: '0 4px 12px rgba(107,79,28,0.1)',
                            }}>
                                <h4 style={{ margin: '0 0 0.8rem', color: 'var(--ad-ink)', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    🛒 {t('Košarica', 'Cart')}
                                    <span style={{ background: 'var(--ad-acc)', color: '#fff', padding: '2px 9px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 700 }}>{cart.length}</span>
                                </h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                                    {cart.map((it) => {
                                        const updateQty = (next: number) => {
                                            const q = Math.max(0, Math.min(it.available, next));
                                            if (q === 0) {
                                                setCart((c) => c.filter((x) => x.product_id !== it.product_id));
                                            } else {
                                                setCart((c) => c.map((x) => x.product_id === it.product_id ? { ...x, qty: q } : x));
                                            }
                                        };
                                        return (
                                        <div key={it.product_id} style={{ background: '#fff', borderRadius: 10, padding: '0.65rem 0.8rem', boxShadow: '0 1px 3px rgba(107,79,28,0.08)', display: 'flex', flexDirection: 'column', gap: 8, fontSize: '0.86rem' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <strong style={{ color: 'var(--ad-ink)', display: 'block', fontSize: '0.82rem' }}>{it.sku}</strong>
                                                    <span style={{ color: 'rgba(0,0,0,0.7)', fontSize: '0.78rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.product_name}</span>
                                                </div>
                                                <button type="button" onClick={() => setCart((c) => c.filter((x) => x.product_id !== it.product_id))} style={{
                                                    background: 'rgba(177,64,64,0.08)',
                                                    color: '#b14040', border: '1px solid rgba(177,64,64,0.25)',
                                                    width: 26, height: 26, borderRadius: 6, cursor: 'pointer',
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: '0.9rem', lineHeight: 1, padding: 0, flexShrink: 0,
                                                }} title={t('Ukloni iz košarice', 'Remove from cart')}>🗑</button>
                                            </div>
                                            <div style={{
                                                display: 'flex', alignItems: 'center', gap: 0,
                                                background: '#f8fafc', borderRadius: 8, padding: 3,
                                                border: '1px solid rgba(var(--ad-soft-rgb),0.22)',
                                            }}>
                                                <button type="button" onClick={() => updateQty(it.qty - 1)} style={{
                                                    background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.35)',
                                                    width: 30, height: 30, borderRadius: 6, cursor: 'pointer',
                                                    fontSize: '1.1rem', fontWeight: 700, lineHeight: 1, color: 'var(--ad-ink)',
                                                }}>−</button>
                                                <input type="number" min={0} max={it.available} value={it.qty} onChange={(e) => updateQty(Number(e.target.value) || 0)}
                                                    style={{ width: 60, padding: '4px 0', textAlign: 'center', borderRadius: 0, border: 0, background: 'transparent', fontWeight: 700, color: 'var(--ad-ink)', fontSize: '0.95rem' }} />
                                                <button type="button" disabled={it.qty >= it.available} onClick={() => updateQty(it.qty + 1)} style={{
                                                    background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.35)',
                                                    width: 30, height: 30, borderRadius: 6,
                                                    cursor: it.qty >= it.available ? 'not-allowed' : 'pointer',
                                                    fontSize: '1.1rem', fontWeight: 700, lineHeight: 1, color: 'var(--ad-ink)',
                                                    opacity: it.qty >= it.available ? 0.4 : 1,
                                                }}>+</button>
                                                <span style={{ marginLeft: 'auto', marginRight: 6, color: 'rgba(0,0,0,0.5)', fontSize: '0.75rem' }}>od {it.available}</span>
                                            </div>
                                        </div>
                                    );})}
                                </div>
                                <button type="button" disabled={submittingCart || ! overview.primary_department_id || cart.length === 0} onClick={async () => {
                                    setSubmittingCart(true);
                                    try {
                                        const r = await api<{ ok: boolean; count: number; reservation_order_id: string }>('/warehouse-stock/reservations/cart', token, {
                                            method: 'POST',
                                            body: JSON.stringify({
                                                items: cart.map((c) => ({ inventory_id: activeInv, product_id: c.product_id, quantity: c.qty })),
                                            }),
                                        });
                                        setCart([]);
                                        await reload();
                                        alert(t(`Kreirana narudžba s ${r.count} stavki.`, `Created order with ${r.count} items.`) + ' (' + r.reservation_order_id.slice(0, 8) + ')');
                                    } catch (e) { setError(e instanceof Error ? e.message : ''); }
                                    finally { setSubmittingCart(false); }
                                }} style={{
                                    width: '100%',
                                    background: cart.length === 0 || ! overview.primary_department_id ? '#94a3b8' : 'linear-gradient(135deg, #1f7a36, #15803d)',
                                    color: '#fff', border: 0,
                                    padding: '12px', borderRadius: 10,
                                    cursor: cart.length === 0 || ! overview.primary_department_id ? 'not-allowed' : 'pointer',
                                    fontWeight: 700, fontSize: '0.98rem',
                                    boxShadow: cart.length > 0 ? '0 4px 12px rgba(31,122,54,0.3)' : 'none',
                                    opacity: submittingCart ? 0.6 : 1,
                                    transition: 'all 140ms ease',
                                }}>
                                    {submittingCart ? '⏳ ' + t('Rezerviram…', 'Reserving…') : '✓ ' + t('Rezerviraj', 'Reserve')}
                                </button>
                                <p style={{ margin: '8px 0 0', fontSize: '0.74rem', color: 'rgba(0,0,0,0.6)' }}>
                                    {t('Rezervacija ide na poslovnicu', 'Reservation goes to branch')}: <strong>{overview.primary_department_name ?? '—'}</strong>
                                </p>
                            </aside>
                        ) : null}
                    </div>
                </>
            ) : null}

            {tab === 'reservations' ? (
                <ReservationsAndEmailLog
                    reservations={visibleReservations}
                    isManager={isManager}
                    locale={locale}
                    token={token}
                    onReload={reload}
                    onError={setError}
                    onEmailResult={setEmailResult}
                    onCancel={async (id, reason) => {
                        try { await api(`/warehouse-stock/reservations/${id}/cancel`, token, { method: 'POST', body: JSON.stringify({ cancel_reason: reason }) }); await reload(); }
                        catch (e) { setError(e instanceof Error ? e.message : ''); }
                    }}
                    onReject={async (id, reason) => {
                        try { await api(`/warehouse-stock/reservations/${id}/reject`, token, { method: 'POST', body: JSON.stringify({ cancel_reason: reason }) }); await reload(); }
                        catch (e) { setError(e instanceof Error ? e.message : ''); }
                    }}
                    onFulfill={(r) => setFulfillForm({ open: true, reservation: r, quantity: String(r.quantity), erpRef: '', notes: '' })}
                    onReactivate={async (id) => {
                        try { await api(`/warehouse-stock/reservations/${id}/reactivate`, token, { method: 'POST' }); await reload(); }
                        catch (e) { setError(e instanceof Error ? e.message : ''); }
                    }}
                />
            ) : null}

            {tab === 'settings' && isManager ? (
                <SettingsPanel overview={overview} token={token} onChange={async () => { await reload(); }} setError={setError} />
            ) : null}

            {tab === 'strategy' && isOwner ? <GoldStrategyInputsPanel token={token} /> : null}

            {tab === 'manual_alloc' ? <ManualOrderAllocationPanel token={token} locale={locale} isManager={isManager} /> : null}

            {tab === 'open_orders_aging' ? <OpenOrdersAgingPanel token={token} locale={locale} /> : null}

            {/* Per-user email opt-in (vidljiv svima) */}
            <div style={{ marginTop: '1.4rem', padding: '0.8rem 1rem', background: '#fffbeb', borderRadius: 10, border: '1px solid rgba(var(--ad-soft-rgb),0.22)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.86rem' }}>
                    <input
                        type="checkbox"
                        checked={overview.notify_stock_reservation}
                        onChange={async (e) => {
                            try {
                                await api('/warehouse-stock/me/notification', token, { method: 'PATCH', body: JSON.stringify({ notify_stock_reservation: e.target.checked }) });
                                await reload();
                            } catch (err) { setError(err instanceof Error ? err.message : ''); }
                        }}
                    />
                    <span>📧 {t('Šalji mi email obavijesti za moje rezervacije (uskoro istek, odbijena, otpremljena).', 'Email me about my reservations (expiring soon, rejected, fulfilled).')}</span>
                </label>
            </div>

            {/* Reserve modal */}
            {resForm.open ? (
                <div className="modal-backdrop" onClick={() => setResForm({ ...resForm, open: false })}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h4>{t('Nova rezervacija', 'New reservation')}</h4>
                        <p className="muted">{resForm.product_name}</p>
                        <p style={{ fontSize: '0.84rem' }}>{t('Dostupno', 'Available')}: <strong>{fmtQty(resForm.available)} {resForm.unit ?? ''}</strong></p>
                        <label className="filter-label">{t('Količina', 'Quantity')}<input type="number" step="0.001" value={resForm.qty} onChange={(e) => setResForm({ ...resForm, qty: e.target.value })} /></label>
                        <label className="filter-label">{t('Poslovnica', 'Branch')}
                            <select value={resForm.dept ?? ''} onChange={(e) => setResForm({ ...resForm, dept: Number(e.target.value) })}>
                                {overview.departments.map((d) => <option key={d.id} value={d.id}>{d.code ? `${d.code} · ` : ''}{d.name}</option>)}
                            </select>
                        </label>
                        <label className="filter-label">{t('Napomena', 'Notes')}<textarea rows={2} value={resForm.notes} onChange={(e) => setResForm({ ...resForm, notes: e.target.value })} /></label>
                        <p className="muted" style={{ fontSize: '0.78rem', marginTop: 8 }}>
                            ⏱ {t('Rezervacija automatski ističe za', 'Reservation auto-expires in')} <strong>{overview.settings.auto_expiry_days}</strong> {t('dana', 'days')} {t('ako se ne otpremi.', 'unless fulfilled.')}
                        </p>
                        <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
                            <button type="button" className="compact" onClick={async () => {
                                try {
                                    await api('/warehouse-stock/reservations', token, { method: 'POST', body: JSON.stringify({
                                        inventory_id: activeInv,
                                        product_id: resForm.product_id,
                                        branch_department_id: resForm.dept,
                                        quantity: Number(resForm.qty),
                                        notes: resForm.notes,
                                    }) });
                                    setResForm({ ...resForm, open: false });
                                    await reload();
                                } catch (e) { setError(e instanceof Error ? e.message : ''); }
                            }}>{t('Rezerviraj', 'Reserve')}</button>
                            <button type="button" className="secondary compact" onClick={() => setResForm({ ...resForm, open: false })}>{t('Odustani', 'Cancel')}</button>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* Stock entry modal (manager+) */}
            {stockForm.open && isManager ? (
                <div className="modal-backdrop" onClick={() => setStockForm({ ...stockForm, open: false })}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h4>{stockForm.kind === 'initial' ? t('Početno stanje', 'Initial stock') : t('Korekcija stanja', 'Adjustment')}</h4>
                        {stockForm.product_id == null ? (
                            <label className="filter-label">{t('Artikl', 'Product')}
                                <select value={stockForm.product_id ?? ''} onChange={(e) => {
                                    const id = Number(e.target.value);
                                    const p = productMap[id];
                                    setStockForm({ ...stockForm, product_id: id, product_sku: p?.sku ?? '', product_name: p?.name_hr ?? '' });
                                }}>
                                    <option value="">— {t('odaberi', 'select')} —</option>
                                    {overview.products.map((p) => <option key={p.id} value={p.id}>{p.sku ?? '—'} · {p.name_hr}</option>)}
                                </select>
                            </label>
                        ) : (
                            <p className="muted">{stockForm.product_sku} · {stockForm.product_name}</p>
                        )}
                        <label className="filter-label">{t('Tip', 'Kind')}
                            <select value={stockForm.kind} onChange={(e) => setStockForm({ ...stockForm, kind: e.target.value as any })}>
                                <option value="initial">{t('Početno (set)', 'Initial')}</option>
                                <option value="adjustment">{t('Korekcija (+/−)', 'Adjustment')}</option>
                            </select>
                        </label>
                        <label className="filter-label">{stockForm.kind === 'initial' ? t('Količina', 'Quantity') : t('Delta (+/−)', 'Delta (+/−)')}
                            <input type="number" step="0.001" value={stockForm.delta} onChange={(e) => setStockForm({ ...stockForm, delta: e.target.value })} />
                        </label>
                        <label className="filter-label">{t('Napomena', 'Notes')}<textarea rows={2} value={stockForm.notes} onChange={(e) => setStockForm({ ...stockForm, notes: e.target.value })} /></label>
                        <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
                            <button type="button" className="compact" onClick={async () => {
                                try {
                                    await api('/warehouse-stock/stock-entries', token, { method: 'POST', body: JSON.stringify({
                                        inventory_id: activeInv,
                                        product_id: stockForm.product_id,
                                        kind: stockForm.kind,
                                        delta: Number(stockForm.delta),
                                        notes: stockForm.notes,
                                    }) });
                                    setStockForm({ ...stockForm, open: false });
                                    await reload();
                                } catch (e) { setError(e instanceof Error ? e.message : ''); }
                            }}>{t('Spremi', 'Save')}</button>
                            <button type="button" className="secondary compact" onClick={() => setStockForm({ ...stockForm, open: false })}>{t('Odustani', 'Cancel')}</button>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* Fulfill modal (manager+) */}
            {fulfillForm.open && fulfillForm.reservation ? (
                <div className="modal-backdrop" onClick={() => setFulfillForm({ ...fulfillForm, open: false })}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h4>🚚 {t('Otpremi rezervaciju', 'Fulfill reservation')}</h4>
                        <p className="muted">{fulfillForm.reservation.product_sku} · {fulfillForm.reservation.product_name_hr} → {fulfillForm.reservation.department_name}</p>
                        <label className="filter-label">{t('Otpremljena količina', 'Fulfilled quantity')}<input type="number" step="0.001" value={fulfillForm.quantity} onChange={(e) => setFulfillForm({ ...fulfillForm, quantity: e.target.value })} /></label>
                        <label className="filter-label">{t('ERP referenca (međuskladišnica) — preporučeno', 'ERP reference (transfer doc) — encouraged')}<input type="text" placeholder="npr. MS-2026-00123" value={fulfillForm.erpRef} onChange={(e) => setFulfillForm({ ...fulfillForm, erpRef: e.target.value })} /></label>
                        <label className="filter-label">{t('Napomena', 'Notes')}<textarea rows={2} value={fulfillForm.notes} onChange={(e) => setFulfillForm({ ...fulfillForm, notes: e.target.value })} /></label>
                        <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
                            <button type="button" className="compact" style={{ background: '#1f7a36', color: '#fff' }} onClick={async () => {
                                try {
                                    await api(`/warehouse-stock/reservations/${fulfillForm.reservation!.id}/fulfill`, token, { method: 'POST', body: JSON.stringify({
                                        fulfilled_quantity: Number(fulfillForm.quantity),
                                        erp_reference: fulfillForm.erpRef || null,
                                        notes: fulfillForm.notes || null,
                                    }) });
                                    setFulfillForm({ ...fulfillForm, open: false });
                                    await reload();
                                } catch (e) { setError(e instanceof Error ? e.message : ''); }
                            }}>✓ {t('Potvrdi otpremu', 'Confirm dispatch')}</button>
                            <button type="button" className="secondary compact" onClick={() => setFulfillForm({ ...fulfillForm, open: false })}>{t('Odustani', 'Cancel')}</button>
                        </div>
                    </div>
                </div>
            ) : null}

            {loading ? <p className="muted" style={{ marginTop: 8 }}>{t('Učitavam…', 'Loading…')}</p> : null}
        </section>
    );
}

type OrderGroup = {
    order_id: string;
    public_code: string | null;
    status: string;
    department_code: string | null;
    department_name: string;
    requested_by_name: string | null;
    requested_by: number;
    reserved_at: string;
    expires_at: string | null;
    inventory_name_hr: string;
    items: Reservation[];
};

function groupReservationsByOrder(rs: Reservation[]): OrderGroup[] {
    const map = new Map<string, OrderGroup>();
    for (const r of rs) {
        const oid = r.reservation_order_id || `single-${r.id}`;
        const existing = map.get(oid);
        if (existing) {
            existing.items.push(r);
        } else {
            map.set(oid, {
                order_id: oid,
                public_code: r.reservation_order_public_code,
                status: r.status,
                department_code: r.department_code,
                department_name: r.department_name,
                requested_by_name: r.requested_by_name,
                requested_by: r.requested_by,
                reserved_at: r.reserved_at,
                expires_at: r.expires_at,
                inventory_name_hr: r.inventory_name_hr,
                items: [r],
            });
        }
    }
    // Sort by reserved_at desc
    return Array.from(map.values()).sort((a, b) => (a.reserved_at > b.reserved_at ? -1 : 1));
}

function ReservationsTable({ reservations, isManager, locale, onCancel, onReject, onFulfill }: {
    reservations: Reservation[];
    isManager: boolean;
    locale: Locale;
    onCancel: (id: number, reason: string) => Promise<void>;
    onReject: (id: number, reason: string) => Promise<void>;
    onFulfill: (r: Reservation) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [filter, setFilter] = useState<string>('pending');
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const filtered = filter === 'all' ? reservations : reservations.filter((r) => r.status === filter);
    const groups = groupReservationsByOrder(filtered);
    const fmtDateTime = (d: string | null) => d ? new Date(d.replace(' ', 'T')).toLocaleString('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
    const fmtExpiresIn = (d: string | null) => {
        if (! d) return '—';
        const ms = new Date(d.replace(' ', 'T')).getTime() - Date.now();
        if (ms < 0) return t('isteklo', 'expired');
        const totalMin = Math.floor(ms / 60000);
        if (totalMin < 60) return `${totalMin}m`;
        const hours = Math.floor(totalMin / 60);
        const mins = totalMin % 60;
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };
    const fmtQty = (n: number | string | null) => {
        if (n == null) return '—';
        const num = Number(n);
        if (! Number.isFinite(num)) return '—';
        return Number.isInteger(num) ? String(num) : num.toFixed(2).replace('.', ',');
    };
    const toggleExp = (oid: string) => setExpanded((s) => { const n = new Set(s); if (n.has(oid)) n.delete(oid); else n.add(oid); return n; });

    return (
        <div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                {['pending', 'fulfilled', 'cancelled', 'rejected', 'expired', 'all'].map((s) => {
                    const cnt = s === 'all'
                        ? new Set(reservations.map((r) => r.reservation_order_id || `single-${r.id}`)).size
                        : new Set(reservations.filter((r) => r.status === s).map((r) => r.reservation_order_id || `single-${r.id}`)).size;
                    return (
                        <button key={s} type="button" onClick={() => setFilter(s)} style={{
                            background: filter === s ? 'var(--ad-ink)' : 'transparent',
                            color: filter === s ? '#fffbeb' : 'var(--ad-ink)',
                            border: '1px solid rgba(var(--ad-soft-rgb),0.4)',
                            padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                        }}>{s} ({cnt})</button>
                    );
                })}
            </div>
            {groups.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'rgba(0,0,0,0.4)', padding: '1rem', fontStyle: 'italic' }}>{t('Nema narudžbi.', 'No orders.')}</p>
            ) : null}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {groups.map((g) => {
                    const statusColor = g.status === 'pending' ? 'var(--ad-acc)' : g.status === 'fulfilled' ? '#1f7a36' : '#b14040';
                    const isOpen = expanded.has(g.order_id);
                    const firstItem = g.items[0];
                    const totalQty = g.items.reduce((s, r) => s + Number(r.quantity || 0), 0);
                    return (
                        <div key={g.order_id} style={{
                            border: g.status === 'pending' ? '1.5px solid rgba(var(--ad-acc-rgb),0.4)' : '1px solid rgba(var(--ad-soft-rgb),0.2)',
                            borderRadius: 12, background: g.status === 'pending' ? 'linear-gradient(180deg, #fffbeb 0%, rgba(254,243,199,0.3) 100%)' : '#fff',
                            overflow: 'hidden',
                        }}>
                            <div onClick={() => toggleExp(g.order_id)} style={{
                                padding: '0.7rem 1rem', cursor: 'pointer',
                                display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto auto', gap: 12, alignItems: 'center',
                            }}>
                                <span style={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.4)' }}>{isOpen ? '▾' : '▸'}</span>
                                <div>
                                    <strong style={{ color: 'var(--ad-ink)', fontSize: '1rem' }}>{g.public_code ?? g.order_id.slice(0, 8)}</strong>
                                    <span style={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.55)', marginLeft: 8 }}>· {g.items.length} {g.items.length === 1 ? t('stavka', 'item') : t('stavki', 'items')}</span>
                                </div>
                                <span style={{ fontSize: '0.82rem', color: 'rgba(0,0,0,0.7)' }}>🏪 {g.department_code ?? ''} {g.department_name}</span>
                                <span style={{ fontSize: '0.82rem', color: 'rgba(0,0,0,0.6)' }}>👤 {g.requested_by_name ?? `#${g.requested_by}`}</span>
                                <span style={{ fontSize: '0.82rem' }}>{g.status === 'pending' && g.expires_at ? <>⏳ <strong style={{ color: 'var(--ad-acc)' }}>{fmtExpiresIn(g.expires_at)}</strong></> : <>📅 {fmtDateTime(g.reserved_at)}</>}</span>
                                <span style={{ color: statusColor, fontWeight: 700, fontSize: '0.86rem' }}>● {g.status}</span>
                            </div>
                            {isOpen ? (
                                <div style={{ padding: '0 1rem 0.8rem', borderTop: '1px solid rgba(var(--ad-soft-rgb),0.18)' }}>
                                    <table className="admin-user-table" style={{ margin: '0.5rem 0' }}>
                                        <thead><tr>
                                            <th>{t('Šifra', 'SKU')}</th>
                                            <th>{t('Artikl', 'Product')}</th>
                                            <th>{t('Skladište', 'Inventory')}</th>
                                            <th style={{ textAlign: 'right' }}>{t('Količina', 'Qty')}</th>
                                            <th />
                                        </tr></thead>
                                        <tbody>
                                            {g.items.map((r) => (
                                                <tr key={r.id}>
                                                    <td><strong>{r.product_sku}</strong></td>
                                                    <td>{r.product_name_hr}</td>
                                                    <td>{r.inventory_name_hr}</td>
                                                    <td style={{ textAlign: 'right' }}>{fmtQty(r.quantity)} kom{r.fulfilled_quantity != null ? <span style={{ display: 'block', fontSize: '0.7rem', color: '#1f7a36' }}>({t('otp.', 'fulf.')} {fmtQty(r.fulfilled_quantity)})</span> : null}</td>
                                                    <td style={{ display: 'flex', gap: 4 }}>
                                                        {r.status === 'pending' ? (
                                                            <>
                                                                {isManager ? (
                                                                    <>
                                                                        <button type="button" className="compact" style={{ background: '#1f7a36', color: '#fff' }} title={t('Otpremi', 'Fulfill')} onClick={() => onFulfill(r)}>🚚</button>
                                                                        <button type="button" className="secondary compact" style={{ color: '#b14040' }} title={t('Odbij', 'Reject')} onClick={async () => {
                                                                            const reason = prompt(t('Razlog odbijanja:', 'Reason:')) ?? '';
                                                                            await onReject(r.id, reason);
                                                                        }}>✗</button>
                                                                    </>
                                                                ) : null}
                                                                <button type="button" className="secondary compact" title={t('Otkaži', 'Cancel')} onClick={async () => {
                                                                    if (! confirm(t('Otkazati stavku rezervacije?', 'Cancel item?'))) return;
                                                                    await onCancel(r.id, '');
                                                                }}>↺</button>
                                                            </>
                                                        ) : null}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

type LowTrigger = {
    id: number;
    inventory_id: number;
    product_id: number;
    threshold_qty: number;
    armed: boolean | number;
    last_fired_at: string | null;
    fire_count: number;
    product_sku: string | null;
    product_name_hr: string;
    inventory_name_hr: string;
    user_name: string | null;
    user_email: string | null;
    current_qty: number | string | null;
};

function SettingsPanel({ overview, token, onChange, setError }: { overview: Overview; token: string; onChange: () => Promise<void>; setError: (s: string) => void }) {
    const [days, setDays] = useState(overview.settings.auto_expiry_days);
    const [triggers, setTriggers] = useState<LowTrigger[]>([]);
    const [newProd, setNewProd] = useState<string>('');
    const [newInv, setNewInv] = useState<number | null>(null);
    const [newThr, setNewThr] = useState<string>('');

    const loadTriggers = useCallback(async () => {
        try {
            const r = await api<{ rows: LowTrigger[] }>('/warehouse-stock/low-triggers', token);
            setTriggers(r.rows);
        } catch (e) { setError(e instanceof Error ? e.message : ''); }
    }, [token, setError]);
    useEffect(() => { void loadTriggers(); }, [loadTriggers]);

    // Proizvodi s trenutnim stanjem > 0 (po skladištu) — eligible za novi trigger.
    const eligibleProducts = overview.products.filter((p) => {
        const lvl = overview.levels.find((l) => Number(l.product_id) === Number(p.id) && Number(l.inventory_id) === Number(newInv));
        return Number(lvl?.total_quantity ?? 0) > 0;
    });

    async function addTrigger() {
        if (! newProd || ! newInv || ! newThr) return;
        try {
            await api('/warehouse-stock/low-triggers', token, { method: 'POST', body: JSON.stringify({
                inventory_id: newInv, product_id: Number(newProd), threshold_qty: Number(newThr),
            }) });
            setNewProd(''); setNewThr('');
            await loadTriggers();
        } catch (e) { setError(e instanceof Error ? e.message : ''); }
    }

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: '1rem' }}>
            <div style={{ padding: '1rem', background: '#fffbeb', borderRadius: 12, border: '1px solid rgba(var(--ad-soft-rgb),0.22)' }}>
                <h4 style={{ marginTop: 0, color: 'var(--ad-ink)' }}>⚙ Auto-expiry rezervacije</h4>
                <label className="filter-label">Auto-expiry (dana, općeniti default)
                    <input type="number" min={1} max={60} value={days} onChange={(e) => setDays(Number(e.target.value))} />
                </label>
                <button type="button" className="compact" onClick={async () => {
                    try {
                        await api('/warehouse-stock/settings', token, { method: 'PATCH', body: JSON.stringify({ auto_expiry_days: days }) });
                        await onChange();
                    } catch (e) { setError(e instanceof Error ? e.message : ''); }
                }}>Spremi</button>
                <p className="muted" style={{ fontSize: '0.74rem', marginTop: 8 }}>
                    Rezervacija koja se ne otpremi prelazi u istekle nakon ovoliko dana. Istekla rezervacija s aktivnom oznakom "imam ju" i dalje drži robu u pokrivenosti (40.6, 40.61).
                </p>
            </div>

            <div style={{ gridColumn: '1 / -1', padding: '1.2rem 1.4rem', background: '#fff', borderRadius: 14, border: '1px solid rgba(var(--ad-soft-rgb),0.28)', boxShadow: '0 1px 3px rgba(107,79,28,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: '1rem' }}>
                    <div>
                        <h4 style={{ margin: 0, color: 'var(--ad-ink)', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                            🔔 Low-stock email trigeri
                            <span style={{ background: 'var(--ad-ink)', color: '#fffbeb', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700 }}>{triggers.length}</span>
                        </h4>
                        <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'rgba(0,0,0,0.6)', maxWidth: 560 }}>
                            Postavi prag po proizvodu — kad stanje padne na/ispod, dolazi <strong>JEDAN</strong> email. Sustav se re-armira kad stanje opet poraste iznad praga.
                        </p>
                    </div>
                </div>

                {/* Novi trigger — clean grid form */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(160px, 1fr) minmax(220px, 2fr) 130px auto',
                    gap: 10, alignItems: 'end',
                    padding: '0.9rem 1rem',
                    background: 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.4) 100%)',
                    border: '1px solid rgba(var(--ad-soft-rgb),0.32)',
                    borderRadius: 10, marginBottom: '1rem',
                }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ fontSize: '0.7rem', color: 'var(--ad-ink)', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>📦 Skladište</span>
                        <select value={newInv ?? ''} onChange={(e) => { setNewInv(e.target.value ? Number(e.target.value) : null); setNewProd(''); }} style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)', background: '#fff' }}>
                            <option value="">— odaberi —</option>
                            {overview.inventories.map((i) => <option key={i.id} value={i.id}>{i.name_hr}</option>)}
                        </select>
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ fontSize: '0.7rem', color: 'var(--ad-ink)', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>💎 Artikl (samo stanje &gt; 0)</span>
                        <select value={newProd} onChange={(e) => setNewProd(e.target.value)} disabled={! newInv} style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)', background: newInv ? '#fff' : '#f3f4f6' }}>
                            <option value="">— odaberi —</option>
                            {eligibleProducts.map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name_hr}</option>)}
                        </select>
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ fontSize: '0.7rem', color: 'var(--ad-ink)', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>📉 Prag</span>
                        <input type="number" min={0.001} step={0.001} value={newThr} onChange={(e) => setNewThr(e.target.value)} placeholder="npr. 5" style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)', background: '#fff' }} />
                    </label>
                    <button type="button" disabled={! newProd || ! newInv || ! newThr} onClick={() => void addTrigger()} style={{
                        background: (! newProd || ! newInv || ! newThr) ? '#94a3b8' : 'linear-gradient(135deg, var(--ad-acc), #b8895a)',
                        color: '#fff', border: 0, padding: '8px 18px', borderRadius: 8,
                        cursor: (! newProd || ! newInv || ! newThr) ? 'not-allowed' : 'pointer',
                        fontWeight: 700, fontSize: '0.88rem', height: 38,
                    }}>+ Dodaj</button>
                </div>

                {/* Lista postojećih */}
                {triggers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '1.6rem 0.5rem', color: 'rgba(0,0,0,0.45)', fontSize: '0.86rem', background: '#fafafa', borderRadius: 10, border: '1px dashed rgba(var(--ad-soft-rgb),0.3)' }}>
                        🔕 <strong style={{ color: 'rgba(0,0,0,0.6)' }}>Nema postavljenih trigera.</strong> Dodaj prvi gore ↑
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {triggers.map((tr) => {
                            const cur = Number(tr.current_qty ?? 0);
                            const thr = Number(tr.threshold_qty);
                            const isArmed = !!tr.armed;
                            const below = cur <= thr;
                            return (
                                <div key={tr.id} style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'minmax(220px, 2fr) minmax(140px, 1fr) 90px 110px 130px 44px',
                                    gap: 10, alignItems: 'center',
                                    padding: '0.75rem 0.95rem',
                                    background: below ? 'rgba(177,64,64,0.05)' : '#fff',
                                    border: below ? '1px solid rgba(177,64,64,0.25)' : '1px solid rgba(var(--ad-soft-rgb),0.22)',
                                    borderLeft: below ? '4px solid #b14040' : '4px solid transparent',
                                    borderRadius: 10,
                                }}>
                                    <div style={{ minWidth: 0 }}>
                                        <strong style={{ color: 'var(--ad-ink)', fontSize: '0.86rem', display: 'block' }}>{tr.product_sku}</strong>
                                        <span style={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.65)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tr.product_name_hr}</span>
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: 'rgba(0,0,0,0.7)' }}>📦 {tr.inventory_name_hr}</div>
                                    <div style={{ textAlign: 'center', padding: '4px 8px', background: 'rgba(var(--ad-soft-rgb),0.1)', borderRadius: 6 }}>
                                        <div style={{ fontSize: '0.62rem', color: 'rgba(0,0,0,0.5)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Prag</div>
                                        <strong style={{ color: 'var(--ad-ink)', fontSize: '0.95rem' }}>{thr}</strong>
                                    </div>
                                    <div style={{ textAlign: 'center', padding: '4px 8px', background: below ? 'rgba(177,64,64,0.1)' : 'rgba(31,122,54,0.08)', borderRadius: 6 }}>
                                        <div style={{ fontSize: '0.62rem', color: 'rgba(0,0,0,0.5)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Trenutno</div>
                                        <strong style={{ color: below ? '#b14040' : '#1f7a36', fontSize: '1.05rem' }}>{cur}</strong>
                                    </div>
                                    <div>
                                        {isArmed ? (
                                            <span style={{ background: 'rgba(31,122,54,0.12)', color: '#1f7a36', padding: '4px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em' }}>🟢 ARMED</span>
                                        ) : (
                                            <div>
                                                <span style={{ background: 'rgba(108,117,125,0.15)', color: '#6c757d', padding: '4px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em' }}>📧 POSLANO</span>
                                                {tr.last_fired_at ? <div style={{ fontSize: '0.68rem', color: 'rgba(0,0,0,0.4)', marginTop: 3 }}>{new Date(tr.last_fired_at.replace(' ', 'T')).toLocaleDateString('hr-HR')}</div> : null}
                                            </div>
                                        )}
                                    </div>
                                    <button type="button" onClick={async () => {
                                        if (! confirm('Obrisati trigger?')) return;
                                        try { await api(`/warehouse-stock/low-triggers/${tr.id}`, token, { method: 'DELETE' }); await loadTriggers(); }
                                        catch (e) { setError(e instanceof Error ? e.message : ''); }
                                    }} style={{
                                        background: 'rgba(177,64,64,0.08)', color: '#b14040', border: '1px solid rgba(177,64,64,0.25)',
                                        padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: '0.92rem',
                                        height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    }} title="Obriši trigger">🗑</button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

type EmailLogRow = {
    id: number;
    from_email: string;
    from_name: string | null;
    subject: string | null;
    received_at: string;
    has_pdf: boolean | number;
    pdf_filename: string | null;
    match_status: 'auto_fulfilled' | 'mismatch' | 'no_pdf' | 'no_user' | 'no_pending' | 'error';
    matched_reservation_order_public_code: string | null;
    items_count: number;
    reply_sent: boolean | number;
    notes: string | null;
};

function ReservationsAndEmailLog(props: {
    reservations: Reservation[];
    isManager: boolean;
    locale: Locale;
    token: string;
    onReload: () => Promise<void> | void;
    onError: (s: string) => void;
    onEmailResult: (r: { ok: boolean; output: string } | null) => void;
    onCancel: (id: number, reason: string) => Promise<void>;
    onReject: (id: number, reason: string) => Promise<void>;
    onFulfill: (r: Reservation) => void;
    onReactivate: (id: number) => Promise<void>;
}) {
    const { isManager, locale, token, onReload, onError, onEmailResult } = props;
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const [fetchingEmail, setFetchingEmail] = useState(false);
    const [resPage, setResPage] = useState(1);
    const [logRows, setLogRows] = useState<EmailLogRow[]>([]);
    const [logTotal, setLogTotal] = useState(0);
    const [logPage, setLogPage] = useState(1);
    const LIMIT = 20;

    const loadLog = useCallback(async (page: number) => {
        if (! isManager) return;
        try {
            const r = await api<{ rows: EmailLogRow[]; total: number }>(`/warehouse-stock/email-log?limit=${LIMIT}&offset=${(page - 1) * LIMIT}`, token);
            setLogRows(r.rows);
            setLogTotal(r.total);
        } catch (e) { onError(e instanceof Error ? e.message : ''); }
    }, [token, onError, isManager]);

    useEffect(() => { void loadLog(logPage); }, [loadLog, logPage]);

    // Soft auto-refresh svakih 8s — povlači zadnje stanje i email log bez F5.
    useEffect(() => {
        const id = setInterval(() => {
            void onReload();
            void loadLog(logPage);
        }, 8000);
        return () => clearInterval(id);
    }, [onReload, loadLog, logPage]);

    return (
        <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <h4 style={{ margin: 0, color: 'var(--ad-ink)', flex: 1 }}>📋 {t('Pregled rezervacija', 'Reservations')}</h4>
                <button type="button" className="secondary compact" onClick={() => { void onReload(); void loadLog(logPage); }}>🔄 {t('Osvježi', 'Refresh')}</button>
                {isManager ? (
                    <button type="button" className="secondary compact" disabled={fetchingEmail} onClick={async () => {
                        setFetchingEmail(true);
                        onEmailResult(null);
                        try {
                            const r = await api<{ ok: boolean; output: string }>('/warehouse-stock/fetch-email', token, { method: 'POST' });
                            await onReload();
                            await loadLog(1); setLogPage(1);
                            onEmailResult({ ok: r.ok, output: r.output ?? '' });
                        } catch (e) {
                            onEmailResult({ ok: false, output: e instanceof Error ? e.message : 'fail' });
                        }
                        finally { setFetchingEmail(false); }
                    }}>{fetchingEmail ? '⏳' : '📧'} {t('Dohvati email', 'Fetch email')}</button>
                ) : null}
            </div>
            <PaginatedReservations {...props} page={resPage} setPage={setResPage} limit={LIMIT} emailLog={logRows} />

            {isManager ? (
                <div style={{ marginTop: '1.4rem', borderTop: '2px solid rgba(var(--ad-soft-rgb),0.22)', paddingTop: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <h4 style={{ margin: 0, color: 'var(--ad-ink)' }}>📧 {t('Zadnjih mailova', 'Recent emails')}</h4>
                        <span style={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.5)' }}>· {logTotal} {t('ukupno', 'total')}</span>
                    </div>
                    <table className="admin-user-table">
                        <thead><tr>
                            <th>{t('Match', 'Match')}</th>
                            <th>{t('Od', 'From')}</th>
                            <th>{t('Subject', 'Subject')}</th>
                            <th>PDF</th>
                            <th>{t('Stavki', 'Items')}</th>
                            <th>{t('Stigao', 'Received')}</th>
                            <th>{t('Detalji', 'Details')}</th>
                        </tr></thead>
                        <tbody>
                            {logRows.length === 0 ? <tr><td colSpan={7} style={{ textAlign: 'center', color: 'rgba(0,0,0,0.4)', padding: '0.8rem', fontStyle: 'italic' }}>{t('Nema mailova.', 'No emails.')}</td></tr> : null}
                            {logRows.map((m) => {
                                const status = m.match_status;
                                const bg = status === 'auto_fulfilled' ? 'rgba(31,122,54,0.08)' : status === 'mismatch' ? 'rgba(177,64,64,0.10)' : status === 'no_pending' || status === 'no_user' ? 'rgba(177,64,64,0.08)' : status === 'no_pdf' ? 'rgba(var(--ad-soft-rgb),0.10)' : 'rgba(108,117,125,0.08)';
                                const badge = status === 'auto_fulfilled'
                                    ? <span style={{ background: '#1f7a36', color: '#fff', padding: '3px 8px', borderRadius: 12, fontSize: '0.74rem', fontWeight: 700 }}>✓ MATCH → {m.matched_reservation_order_public_code}</span>
                                    : status === 'mismatch' ? <span style={{ background: '#b14040', color: '#fff', padding: '3px 8px', borderRadius: 12, fontSize: '0.74rem', fontWeight: 700 }}>✗ NEMA MATCH</span>
                                    : status === 'no_pending' ? <span style={{ background: '#b14040', color: '#fff', padding: '3px 8px', borderRadius: 12, fontSize: '0.74rem', fontWeight: 700 }}>✗ NEMA REZERVACIJE</span>
                                    : status === 'no_user' ? <span style={{ background: '#b14040', color: '#fff', padding: '3px 8px', borderRadius: 12, fontSize: '0.74rem', fontWeight: 700 }}>✗ NEPOZNAT POŠILJATELJ</span>
                                    : status === 'no_pdf' ? <span style={{ background: 'var(--ad-acc)', color: '#fff', padding: '3px 8px', borderRadius: 12, fontSize: '0.74rem', fontWeight: 700 }}>⚠ BEZ PDF</span>
                                    : <span style={{ background: '#6c757d', color: '#fff', padding: '3px 8px', borderRadius: 12, fontSize: '0.74rem', fontWeight: 700 }}>✗ ERROR</span>;
                                return (
                                    <tr key={m.id} style={{ background: bg }}>
                                        <td>{badge}</td>
                                        <td style={{ fontSize: '0.84rem' }}>{m.from_email}</td>
                                        <td style={{ fontSize: '0.82rem' }}>{m.subject ?? '—'}</td>
                                        <td>{m.has_pdf ? '📎' : '—'}{m.pdf_filename ? <span className="muted" style={{ fontSize: '0.72rem', display: 'block' }}>{m.pdf_filename}</span> : null}</td>
                                        <td style={{ textAlign: 'right' }}>{m.items_count}</td>
                                        <td style={{ fontSize: '0.78rem' }}>{new Date(m.received_at.replace(' ', 'T')).toLocaleString('hr-HR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                                        <td style={{ fontSize: '0.74rem', color: 'rgba(0,0,0,0.6)' }}>{m.notes ?? '—'}{m.reply_sent ? <span style={{ display: 'block', color: 'var(--ad-ink)' }}>↩ reply</span> : null}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {logTotal > LIMIT ? (
                        <Pagination total={logTotal} page={logPage} setPage={setLogPage} limit={LIMIT} locale={locale} />
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

function Pagination({ total, page, setPage, limit, locale }: { total: number; page: number; setPage: (p: number) => void; limit: number; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const pages = Math.max(1, Math.ceil(total / limit));
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', marginTop: 8, fontSize: '0.82rem' }}>
            <span style={{ color: 'rgba(0,0,0,0.55)' }}>{t('Stranica', 'Page')} {page} / {pages} · {total} {t('ukupno', 'total')}</span>
            <button type="button" className="secondary compact" disabled={page <= 1} onClick={() => setPage(Math.max(1, page - 1))}>‹</button>
            <button type="button" className="secondary compact" disabled={page >= pages} onClick={() => setPage(Math.min(pages, page + 1))}>›</button>
        </div>
    );
}

function PaginatedReservations(props: {
    reservations: Reservation[];
    isManager: boolean;
    locale: Locale;
    page: number;
    setPage: (p: number) => void;
    limit: number;
    emailLog: EmailLogRow[];
    onCancel: (id: number, reason: string) => Promise<void>;
    onReject: (id: number, reason: string) => Promise<void>;
    onFulfill: (r: Reservation) => void;
    onReactivate: (id: number) => Promise<void>;
}) {
    const { reservations, page, setPage, limit, emailLog, locale, isManager, onCancel, onReject, onFulfill, onReactivate } = props;
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [filter, setFilter] = useState<string>('pending');
    const filtered = filter === 'all' ? reservations : reservations.filter((r) => r.status === filter);
    const allGroups = groupReservationsByOrder(filtered);
    const total = allGroups.length;
    const visibleGroups = allGroups.slice((page - 1) * limit, page * limit);
    const matchedOrderCodes = new Set(emailLog.filter((m) => m.match_status === 'auto_fulfilled' && m.matched_reservation_order_public_code).map((m) => m.matched_reservation_order_public_code!));

    return (
        <div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                {['pending', 'fulfilled', 'cancelled', 'rejected', 'expired', 'all'].map((s) => {
                    const cnt = s === 'all'
                        ? new Set(reservations.map((r) => r.reservation_order_id || `single-${r.id}`)).size
                        : new Set(reservations.filter((r) => r.status === s).map((r) => r.reservation_order_id || `single-${r.id}`)).size;
                    return (
                        <button key={s} type="button" onClick={() => { setFilter(s); setPage(1); }} style={{
                            background: filter === s ? 'var(--ad-ink)' : 'transparent',
                            color: filter === s ? '#fffbeb' : 'var(--ad-ink)',
                            border: '1px solid rgba(var(--ad-soft-rgb),0.4)',
                            padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                        }}>{s} ({cnt})</button>
                    );
                })}
            </div>
            {visibleGroups.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'rgba(0,0,0,0.4)', padding: '1rem', fontStyle: 'italic' }}>{t('Nema narudžbi u ovom filteru.', 'No orders.')}</p>
            ) : null}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {visibleGroups.map((g) => {
                    const hasEmailMatch = g.public_code && matchedOrderCodes.has(g.public_code);
                    const hasJsonMatch = g.items.some((it) => (it.erp_reference ?? '').startsWith('JSON:'));
                    // TINYINT preko PDO zna doći kao "1"/"0" string — provjeri eksplicitno (ne !!).
                    const hasOrphan = g.items.some((it) => it.json_orphan === true || it.json_orphan === 1 || it.json_orphan === '1' as unknown);
                    return <ReservationGroupCard key={g.order_id} group={g} hasEmailMatch={!!hasEmailMatch} hasJsonMatch={hasJsonMatch} hasOrphan={hasOrphan} isManager={isManager} locale={locale} onCancel={onCancel} onReject={onReject} onFulfill={onFulfill} onReactivate={onReactivate} />;
                })}
            </div>
            {total > limit ? <Pagination total={total} page={page} setPage={setPage} limit={limit} locale={locale} /> : null}
        </div>
    );
}

function ReservationGroupCard({ group, hasEmailMatch, hasJsonMatch, hasOrphan, isManager, locale, onCancel, onReject, onFulfill, onReactivate }: {
    group: OrderGroup; hasEmailMatch: boolean; hasJsonMatch: boolean; hasOrphan: boolean; isManager: boolean; locale: Locale;
    onCancel: (id: number, reason: string) => Promise<void>;
    onReject: (id: number, reason: string) => Promise<void>;
    onFulfill: (r: Reservation) => void;
    onReactivate: (id: number) => Promise<void>;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [open, setOpen] = useState(false);
    const [expandedItemId, setExpandedItemId] = useState<number | null>(null);
    const statusColor = group.status === 'pending' ? 'var(--ad-acc)' : group.status === 'fulfilled' ? '#1f7a36' : '#b14040';
    const fmtQty = (n: number | string | null) => { if (n == null) return '—'; const x = Number(n); return Number.isInteger(x) ? String(x) : x.toFixed(2).replace('.', ','); };
    const fmtExpiresIn = (d: string | null) => {
        if (! d) return '—';
        const ms = new Date(d.replace(' ', 'T')).getTime() - Date.now();
        if (ms < 0) return t('isteklo', 'expired');
        const m = Math.floor(ms / 60000); if (m < 60) return `${m}m`;
        return `${Math.floor(m / 60)}h ${m % 60}m`;
    };
    const fmtDateTime = (d: string | null) => d ? new Date(d.replace(' ', 'T')).toLocaleString('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

    // JSON match je najpouzdaniji signal (auto-fulfilled iz POS-a) → naglašava se zelenom kao i email.
    // Orphan (prodano bez rezervacije) → crveni alert (najjači prioritet u boji okvira).
    const highlight = hasJsonMatch || hasEmailMatch;
    return (
        <div style={{
            border: hasOrphan ? '2px solid #b14040' : (highlight ? '2px solid #1f7a36' : (group.status === 'pending' ? '1.5px solid rgba(var(--ad-acc-rgb),0.4)' : '1px solid rgba(var(--ad-soft-rgb),0.2)')),
            borderRadius: 12,
            background: hasOrphan ? 'linear-gradient(180deg, rgba(177,64,64,0.06) 0%, #fff 100%)' : (highlight ? 'linear-gradient(180deg, rgba(31,122,54,0.04) 0%, #fff 100%)' : (group.status === 'pending' ? 'linear-gradient(180deg, #fffbeb 0%, rgba(254,243,199,0.3) 100%)' : '#fff')),
            overflow: 'hidden',
        }}>
            <div onClick={() => setOpen((v) => !v)} style={{
                padding: '0.7rem 1rem', cursor: 'pointer',
                display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto auto auto', gap: 10, alignItems: 'center',
            }}>
                <span style={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.4)' }}>{open ? '▾' : '▸'}</span>
                <div>
                    <strong style={{ color: 'var(--ad-ink)', fontSize: '1rem' }}>{group.public_code ?? group.order_id.slice(0, 8)}</strong>
                    <span style={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.55)', marginLeft: 8 }}>· {group.items.length} {group.items.length === 1 ? t('stavka', 'item') : t('stavki', 'items')}</span>
                </div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {hasOrphan ? <span title={t('Komisijska prodaja iz JSON-a BEZ rezervacije — provjeri stanje i obračun', 'Komisijsko JSON sale WITHOUT reservation — verify stock & settlement')} style={{ background: '#b14040', color: '#fff', padding: '3px 8px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 700 }}>🚨 BEZ REZERVACIJE</span> : null}
                    {hasJsonMatch ? <span title={t('Auto-fulfilled iz JSON POS feed-a', 'Auto-fulfilled from JSON POS feed')} style={{ background: '#1f7a36', color: '#fff', padding: '3px 8px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 700 }}>💾 JSON MATCH</span> : null}
                    {hasEmailMatch ? <span title={t('PDF iz maila se podudara s rezervacijom', 'Email PDF matches reservation')} style={{ background: '#1f7a36', color: '#fff', padding: '3px 8px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 700 }}>📧 EMAIL MATCH</span> : null}
                </div>
                <span style={{ fontSize: '0.82rem', color: 'rgba(0,0,0,0.7)' }}>🏪 {group.department_code ?? ''} {group.department_name}</span>
                <span style={{ fontSize: '0.82rem', color: 'rgba(0,0,0,0.6)' }}>👤 {group.requested_by_name ?? `#${group.requested_by}`}</span>
                <span style={{ fontSize: '0.82rem' }}>{group.status === 'pending' && group.expires_at ? <>⏳ <strong style={{ color: 'var(--ad-acc)' }}>{fmtExpiresIn(group.expires_at)}</strong></> : <>📅 {fmtDateTime(group.reserved_at)}</>}</span>
                <span style={{ color: statusColor, fontWeight: 700, fontSize: '0.86rem' }}>● {group.status}</span>
            </div>
            {open ? (
                <div style={{ padding: '0 1rem 0.8rem', borderTop: '1px solid rgba(var(--ad-soft-rgb),0.18)' }}>
                    <table className="admin-user-table" style={{ margin: '0.5rem 0' }}>
                        <thead><tr><th>{t('Šifra', 'SKU')}</th><th>{t('Artikl', 'Product')}</th><th>{t('Skladište', 'Inventory')}</th><th style={{ textAlign: 'right' }}>{t('Količina', 'Qty')}</th><th /></tr></thead>
                        <tbody>
                            {group.items.map((r) => (
                                <React.Fragment key={r.id}>
                                <tr>
                                    <td><strong>{r.product_sku}</strong></td>
                                    <td>{r.product_name_hr}</td>
                                    <td>{r.inventory_name_hr}</td>
                                    <td style={{ textAlign: 'right' }}>{fmtQty(r.quantity)} kom{r.fulfilled_quantity != null ? <span style={{ display: 'block', fontSize: '0.7rem', color: '#1f7a36' }}>({t('otp.', 'fulf.')} {fmtQty(r.fulfilled_quantity)})</span> : null}</td>
                                    <td style={{ display: 'flex', gap: 4 }}>
                                        <button
                                            type="button"
                                            className="secondary compact"
                                            title={t('Detalji (tko, kada, ERP veza)', 'Details (who, when, ERP ref)')}
                                            onClick={() => setExpandedItemId((cur) => cur === r.id ? null : r.id)}
                                            style={{ padding: '2px 8px' }}
                                        >ⓘ</button>
                                        {r.status === 'pending' ? (
                                            <>
                                                {isManager ? (
                                                    <>
                                                        <button type="button" className="compact" style={{ background: '#1f7a36', color: '#fff' }} title={t('Otpremi', 'Fulfill')} onClick={() => onFulfill(r)}>🚚</button>
                                                        <button type="button" className="secondary compact" style={{ color: '#b14040' }} title={t('Odbij', 'Reject')} onClick={async () => { const reason = prompt(t('Razlog odbijanja:', 'Reason:')) ?? ''; await onReject(r.id, reason); }}>✗</button>
                                                    </>
                                                ) : null}
                                                <button type="button" className="secondary compact" title={t('Otkaži', 'Cancel')} onClick={async () => { if (! confirm(t('Otkazati stavku?', 'Cancel?'))) return; await onCancel(r.id, ''); }}>↺</button>
                                            </>
                                        ) : null}
                                        {r.status === 'expired' && isManager ? (
                                            <>
                                                <button type="button" className="compact" style={{ background: 'var(--ad-acc)', color: '#fff' }} title={t('Vrati u pending (reaktiviraj)', 'Reactivate to pending')} onClick={async () => {
                                                    if (! confirm(t('Vratiti expired rezervaciju u pending?', 'Reactivate expired reservation?'))) return;
                                                    await onReactivate(r.id);
                                                }}>↻</button>
                                                <button type="button" className="compact" style={{ background: '#1f7a36', color: '#fff' }} title={t('Potvrdi / otpremi naknadno', 'Confirm / fulfill manually')} onClick={() => onFulfill(r)}>🚚</button>
                                            </>
                                        ) : null}
                                    </td>
                                </tr>
                                {expandedItemId === r.id ? (
                                    <tr>
                                        <td colSpan={5} style={{ background: 'rgba(var(--ad-soft-rgb),0.08)', borderTop: 0, padding: '8px 14px' }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px 18px', fontSize: '0.8rem', color: 'rgba(0,0,0,0.78)' }}>
                                                <div>
                                                    <span className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t('Rezervirao', 'Reserved by')}</span>
                                                    <div><strong>👤 {r.requested_by_name ?? `#${r.requested_by}`}</strong> · {fmtDateTime(r.reserved_at)}</div>
                                                </div>
                                                {r.fulfilled_at ? (
                                                    <div>
                                                        <span className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#1f7a36' }}>{t('Otpremio', 'Fulfilled by')}</span>
                                                        <div><strong>🚚 {r.fulfilled_by_name ?? `#${r.fulfilled_by}`}</strong> · {fmtDateTime(r.fulfilled_at)}</div>
                                                        {r.fulfilled_quantity != null ? <div className="muted" style={{ fontSize: '0.74rem' }}>{t('Otpremljena količina', 'Fulfilled qty')}: {fmtQty(r.fulfilled_quantity)}</div> : null}
                                                    </div>
                                                ) : null}
                                                {r.erp_reference ? (
                                                    <div>
                                                        <span className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t('ERP veza dokumenta', 'ERP document ref')}</span>
                                                        <div><strong>🔗 {r.erp_reference}</strong></div>
                                                    </div>
                                                ) : null}
                                                {r.cancelled_at ? (
                                                    <div>
                                                        <span className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#b14040' }}>
                                                            {r.status === 'rejected' ? t('Odbio', 'Rejected by') : r.status === 'expired' ? t('Isteklo', 'Expired') : t('Otkazao', 'Cancelled by')}
                                                        </span>
                                                        <div><strong>{r.cancelled_by_name ?? (r.cancelled_by ? `#${r.cancelled_by}` : t('automatski', 'auto'))}</strong> · {fmtDateTime(r.cancelled_at)}</div>
                                                        {r.cancel_reason ? <div className="muted" style={{ fontSize: '0.74rem', fontStyle: 'italic' }}>„{r.cancel_reason}"</div> : null}
                                                    </div>
                                                ) : null}
                                                {r.notes ? (
                                                    <div style={{ gridColumn: '1 / -1' }}>
                                                        <span className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t('Napomene', 'Notes')}</span>
                                                        <div style={{ fontStyle: 'italic' }}>{r.notes}</div>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </td>
                                    </tr>
                                ) : null}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </div>
    );
}

/**
 * ManualOrderAllocationPanel — m.40.3 → poslovnica označava narudžbe za koje fizički ima robu kod sebe.
 * Auto-pozicionira na matičnu poslovnicu (home_warehouse_code).
 * Engine PHASED automatski premjesti označene narudžbe u Phase 1 (L0) s "RUČNA ALOKACIJA" badge.
 */
function ManualOrderAllocationPanel({ token, locale, isManager = false }: { token: string; locale: Locale; isManager?: boolean }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [selectedWh, setSelectedWh] = useState<string[]>([]);
    const [whMenuOpen, setWhMenuOpen] = useState(false);
    const [allWh, setAllWh] = useState<Array<{ code: string; name: string }>>([]);
    const [eligible, setEligible] = useState<Array<{ order_number: string; partner_name: string; item_date: string | null; items: Array<{ item_name: string; qty: number; fine_gold: number }>; total_eur?: number; offer_document_id?: number | null; transferred_elsewhere?: boolean; transferred_in?: boolean; source_branch_name?: string | null; warehouse_code?: string; warehouse_name?: string; annotations?: Array<{ id: number; annotation_type: string; set_at?: string; set_by_name?: string | null; agreed_delivery_date?: string | null; note?: string | null; value?: string | null }> }>>([]);
    const [reserved, setReserved] = useState<any[]>([]);
    const [search, setSearch] = useState('');
    const [statusSel, setStatusSel] = useState<string[]>([]); // prazno = bez filtera = svi
    const [statusMenuOpen, setStatusMenuOpen] = useState(false);
    const [sortKey, setSortKey] = useState<'date' | 'order' | 'partner' | 'items'>('date');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    // "Složi po statusu" (vlasnik 24.09.2026): grupe po lifecycle statusu sa zbrojevima; sklopljene grupe po ključu.
    const [groupByStatus, setGroupByStatus] = useState(false);
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
    const toggleGroup = (key: string) => setCollapsedGroups((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [drawer, setDrawer] = useState<typeof eligible[number] | null>(null);
    // Privremena oznaka "šaljem ovo" (kod izrade međuskladišnice) — nestane na reload (Danijela 18.6.).
    const [markedOrders, setMarkedOrders] = useState<Set<string>>(new Set());
    const toggleMarked = (orderNumber: string) => setMarkedOrders((s) => { const n = new Set(s); n.has(orderNumber) ? n.delete(orderNumber) : n.add(orderNumber); return n; });

    // Pošiljka (parcel) statusi po narudžbi — povučeno iz /parcels/by-orders.
    const [parcelLinks, setParcelLinks] = useState<Record<string, any>>({});

    const [scope, setScope] = useState<'all' | 'assigned'>('assigned');
    // Role-scoped poslovnice: user/manager → samo dodijeljene; administrator → sve.
    useEffect(() => {
        (async () => {
            try {
                const me = await api<{ user: any }>('/auth/me', token);
                const home = me.user?.profile?.home_warehouse_code ?? me.user?.home_warehouse_code;
                const wh = await api<{ warehouses: Array<{ code: string; name: string }>; scope: 'all' | 'assigned' }>(
                    '/gold-cm-planner/branch-reserved/my-warehouses', token);
                const list = wh.warehouses ?? [];
                setAllWh(list);
                setScope(wh.scope ?? 'assigned');
                // Auto-pozicija: home ako je u dopuštenoj listi, inače jedina poslovnica.
                if (home && list.some((w) => w.code === home)) setSelectedWh([home]);
                else if (list.length === 1) setSelectedWh([list[0].code]);
                else setSelectedWh([]);
            } catch { /* */ }
        })();
    }, [token]);

    const load = useCallback(async () => {
        if (selectedWh.length === 0) { setEligible([]); setReserved([]); return; }
        setBusy(true); setErr('');
        try {
            // Batchiranje: umjesto 136 paralelnih poziva (2 * 68 poslovnica) koji zaguse
            // browser queue i PHP-FPM worker pool, salje se po BATCH_SIZE poslovnica istovremeno.
            const BATCH_SIZE = 8;
            const mergedEligible: any[] = [];
            const mergedReserved: any[] = [];
            for (let i = 0; i < selectedWh.length; i += BATCH_SIZE) {
                const batch = selectedWh.slice(i, i + BATCH_SIZE);
                const calls: Array<Promise<{ kind: 'eligible' | 'reserved'; code: string; data: any }>> = [];
                for (const code of batch) {
                    calls.push(api<{ orders: any[] }>(`/gold-cm-planner/branch-reserved/eligible?warehouse_code=${encodeURIComponent(code)}`, token)
                        .then((d) => ({ kind: 'eligible' as const, code, data: d })));
                    calls.push(api<{ rows: any[] }>(`/gold-cm-planner/branch-reserved?warehouse_code=${encodeURIComponent(code)}`, token)
                        .then((d) => ({ kind: 'reserved' as const, code, data: d })));
                }
                const results = await Promise.allSettled(calls);
                for (const r of results) {
                    if (r.status !== 'fulfilled') continue;
                    const res = r.value;
                    if (res.kind === 'eligible') {
                        const whName = allWh.find((w) => w.code === res.code)?.name ?? res.code;
                        for (const o of (res.data.orders ?? [])) {
                            mergedEligible.push({ ...o, warehouse_code: res.code, warehouse_name: whName });
                        }
                    } else {
                        for (const row of (res.data.rows ?? [])) mergedReserved.push(row);
                    }
                }
            }
            setEligible(mergedEligible);
            setReserved(mergedReserved);
        } catch (ex) { setErr(ex instanceof Error ? ex.message : 'API'); }
        finally { setBusy(false); }
    }, [selectedWh, token, allWh]);
    useEffect(() => { void load(); }, [load]);

    // Pošiljka statusi — kad se eligible promijeni, dohvati parcel linkove za prikazane narudžbe.
    useEffect(() => {
        if (eligible.length === 0) { setParcelLinks({}); return; }
        const uniq = Array.from(new Set(eligible.map((o) => o.order_number).filter(Boolean))).slice(0, 600);
        if (uniq.length === 0) { setParcelLinks({}); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await api<{ links: Record<string, any> }>(`/parcels/by-orders?orders=${encodeURIComponent(uniq.join(','))}`, token);
                if (!cancelled) setParcelLinks(res.links ?? {});
            } catch { /* parcel statusi su best-effort; ne ruše panel */ }
        })();
        return () => { cancelled = true; };
    }, [eligible, token]);

    // Pokreni window.prompt capture barkoda i poveži s narudžbom (overwrite mogući).
    const captureBarcode = (orderNumber: string) => {
        const bc = window.prompt(t('Barkod pošiljke za narudžbu', 'Parcel barcode for order') + ' ' + orderNumber + ':');
        if (bc && bc.trim()) {
            mutate('/parcels/link', token, 'POST', { order_number: orderNumber, barcode: bc.trim(), carrier: 'HP' })
                .then((res: any) => setParcelLinks((m) => ({ ...m, [orderNumber]: { order_number: orderNumber, barcode: res.barcode, carrier: res.carrier, ...(res.parcel || {}) } })))
                .catch((e: any) => alert(String(e?.message ?? e)));
        }
    };

    // Kompozitni ključ poslovnica|narudžba — ista narudžba može postojati u više poslovnica.
    const reservedOrders = new Set(reserved.filter((r) => r.is_manual_full_order).map((r) => `${r.warehouse_code}|${r.order_number}`));

    async function markFullOrder(orderNum: string, whc: string) {
        setBusy(true); setErr('');
        try {
            const res = await fetch('/api/gold-cm-planner/branch-reserved/mark-full-order', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, Accept: 'application/json' },
                body: JSON.stringify({ warehouse_code: whc, order_number: orderNum }),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message ?? `HTTP ${res.status}`);
            console.log('[m.40.3 markFullOrder] OK', json);
            await load();
            const unmatched: string[] = Array.isArray(json?.unmatched_items) ? json.unmatched_items : [];
            if (unmatched.length > 0) {
                setErr(`Smješteno za ${orderNum}: ${unmatched.length} stavki nije u 110 ili naziv nije jednoznačan, nisu rezervirane: ${unmatched.join('; ')}`);
            }
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'API';
            console.error('[m.40.3 markFullOrder] ERR', e);
            setErr(`Smješteno FAIL za ${orderNum}: ${msg}`);
            alert(`Greška: ${msg}`);
        } finally { setBusy(false); }
    }
    async function releaseOrder(orderNum: string, whc: string) {
        if (!confirm(t('Otpustiti rezervaciju cijele narudžbe?', 'Release reservation of full order?'))) return;
        setBusy(true); setErr('');
        try {
            const res = await fetch('/api/gold-cm-planner/branch-reserved/order', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, Accept: 'application/json' },
                body: JSON.stringify({ warehouse_code: whc, order_number: orderNum }),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message ?? `HTTP ${res.status}`);
            await load();
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'API';
            setErr(`Release FAIL za ${orderNum}: ${msg}`);
            alert(`Greška: ${msg}`);
        } finally { setBusy(false); }
    }

    // Jedan niz statusa, jedan status po narudžbi (Tonći, mail 01.10.2026; nazivi i legenda u shared/orderStatus).
    const LIFECYCLE: string[] = ORDER_STATUS_TYPES;
    // P&G dopune / T87 (mail UID 663, 18.09.2026): status eksplicitno vidljiv kao chip u tablici, istim bojama kao u OrderDrawer-u.
    const LIFECYCLE_LABEL: Record<string, { label: string; color: string }> = Object.fromEntries(
        ORDER_STATUS_TYPES.map((k) => [k, { label: orderStatusLabel(k, locale), color: ORDER_STATUS[k].color }]),
    );
    // Zadnji dosegnuti korak niza je "aktivni" status (isto pravilo kao #11 u OrderDrawer-u), ne prvi upisani.
    const currentLifecycleStatus = (o: { annotations?: Array<{ annotation_type: string }> }): string | null => {
        const st = currentOrderStatus(o.annotations);
        return st === 'none' ? null : st;
    };
    const STATUS_GROUPS: Array<{ header: string; opts: Array<{ key: string; label: string }> }> = [
        {
            header: t('Status narudžbe', 'Order status'),
            opts: [
                // Ukinuti Isporučeno ostaje u filtru samo dok postoje stare oznake.
                ...ORDER_STATUS_SEQUENCE.filter((k) => k !== 'none').map((k) => ({ key: k, label: orderStatusLabel(k, locale) })),
                { key: 'none', label: orderStatusLabel('none', locale) },
            ],
        },
        {
            header: t('Oznake', 'Flags'),
            opts: [
                { key: 'hitno', label: t('Hitno', 'Urgent') },
                { key: 'express', label: t('Express', 'Express') },
                { key: 'dogovoren_datum', label: t('Dogovoren datum', 'Agreed date') },
                { key: 'na_cekanju_klijent', label: t('Na čekanju', 'On hold') },
                // Mail UID 876 (Ružica preko Vedrana, "Fw: BIS korekcije", 15.10.2026), ispravak 4:
                // filter za narudzbe oznacene za POHRANU (mjesto_isporuke=POHRANA, Faros auto-tag).
                { key: 'pohrana', label: t('POHRANA', 'STORAGE') },
            ],
        },
        {
            header: t('Alokacija', 'Allocation'),
            opts: [
                { key: 'reserved', label: t('Rezervirano', 'Reserved') },
                { key: 'free', label: t('Slobodno', 'Free') },
                { key: 'transferred_in', label: t('Prebačeno k tebi', 'Transferred in') },
                { key: 'transferred_away', label: t('Prebačeno drugdje', 'Transferred away') },
            ],
        },
    ];
    const ALL_STATUS_KEYS = STATUS_GROUPS.flatMap((g) => g.opts.map((o) => o.key));
    const filtered = eligible.filter((o) => {
        // Grupirani multi-select status/oznake/alokacija filter (OR semantika preko označenih ključeva).
        if (statusSel.length > 0) {
            const annTypes = new Set((o.annotations ?? []).map((a: any) => a.annotation_type));
            const reserved = reservedOrders.has(`${(o as any).warehouse_code}|${o.order_number}`) || annTypes.has('imam_ju');
            const transferredAway = o.transferred_elsewhere === true;
            const transferredIn = o.transferred_in === true;
            const hasLifecycle = LIFECYCLE.some((k) => annTypes.has(k));
            const passes = statusSel.some((key) => {
                // Status po aktivnom koraku (jedan status po narudžbi), oznake po prisutnosti.
                if (LIFECYCLE.includes(key)) return currentLifecycleStatus(o) === key;
                if (['hitno', 'express', 'dogovoren_datum', 'na_cekanju_klijent'].includes(key)) return annTypes.has(key);
                // POHRANA je VRIJEDNOST mjesto_isporuke oznake, ne zaseban annotation_type.
                if (key === 'pohrana') return (o.annotations ?? []).some((a: any) => a.annotation_type === 'mjesto_isporuke' && String(a.value ?? '').trim().toUpperCase() === 'POHRANA');
                if (key === 'none') return !hasLifecycle;
                if (key === 'reserved') return reserved;
                if (key === 'free') return !reserved && !transferredAway && !transferredIn && !hasLifecycle;
                if (key === 'transferred_in') return transferredIn;
                if (key === 'transferred_away') return transferredAway;
                return false;
            });
            if (!passes) return false;
        }
        // Tekstualna pretraga (AND sa status filterom).
        if (!search.trim()) return true;
        const s = search.toLowerCase();
        const hay = `${o.order_number} ${o.partner_name ?? ''} ${o.items.map((i) => i.item_name).join(' ')}`.toLowerCase();
        return hay.includes(s);
    });
    const sorted = [...filtered].sort((a, b) => {
        const mul = sortDir === 'asc' ? 1 : -1;
        switch (sortKey) {
            case 'date': return mul * String(a.item_date ?? '').localeCompare(String(b.item_date ?? ''));
            case 'order': return mul * String(a.order_number).localeCompare(String(b.order_number));
            case 'partner': return mul * String(a.partner_name ?? '').localeCompare(String(b.partner_name ?? ''));
            case 'items': return mul * (a.items.length - b.items.length);
        }
    });
    const toggleSort = (k: typeof sortKey) => {
        if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        else { setSortKey(k); setSortDir(k === 'order' || k === 'partner' ? 'asc' : 'desc'); }
    };
    const sortArrow = (k: typeof sortKey) => sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    async function exportXls() {
        const rows = sorted.map((o: any) => {
            const annTypes = new Set((o.annotations ?? []).map((a: any) => a.annotation_type));
            const reserved = reservedOrders.has(`${o.warehouse_code}|${o.order_number}`) || annTypes.has('imam_ju');
            const status = o.transferred_elsewhere ? 'Prebačeno drugdje' : o.transferred_in ? 'Prebačeno k tebi' : reserved ? 'Rezervirano (smješteno)' : 'Slobodno';
            // Faza = lifecycle oznaka (isto kao ekranski badge). Bez ovoga isporučena narudžba
            // izgleda "Slobodno" u tablici iako na ekranu ima ✅ Isporučeno (Karlovac 451/2026).
            const faza = (() => { const st = currentLifecycleStatus(o); return st ? orderStatusLabel(st, locale) : ''; })();
            // items_count = broj RAZLIČITIH proizvoda, items_text = zbrojene količine po proizvodu.
            const agg = aggregateOrderItems(o.items);
            return { item_date: o.item_date ? String(o.item_date).slice(0, 10) : '', warehouse_name: o.warehouse_name ?? o.warehouse_code ?? '', order_number: o.order_number, partner_name: o.partner_name ?? '', items_count: agg.length, items_text: orderItemsText(o.items).slice(0, 600), status, faza };
        });
        const res = await fetch('/api/gold-cm-planner/branch-reserved/export', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, Accept: '*/*' }, body: JSON.stringify({ rows }) });
        if (!res.ok) { alert('Export FAIL: ' + res.status); return; }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'rucna_alokacija.xlsx'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    }

    // Render ćelije "Pošiljka" za jednu narudžbu.
    const renderParcelCell = (o: { order_number: string }) => {
        const link = parcelLinks[o.order_number];
        if (!link) {
            return (
                <button type="button"
                    onClick={(e) => { e.stopPropagation(); captureBarcode(o.order_number); }}
                    style={{ background: '#fffdf6', border: '1px solid rgba(var(--ad-soft-rgb),0.5)', borderRadius: 6, padding: '2px 8px', fontSize: '0.7rem', cursor: 'pointer' }}>
                    📦 + {t('barkod', 'barcode')}
                </button>
            );
        }
        if (!link.status_norm) {
            // Barkod uhvaćen, izvještaj kurira još nije uvezen.
            return (
                <span onClick={(e) => { e.stopPropagation(); captureBarcode(o.order_number); }}
                    className="muted" title={t('Klikni za ponovni unos barkoda', 'Click to re-enter barcode')}
                    style={{ fontSize: '0.72rem', cursor: 'pointer' }}>
                    📦 {link.barcode} ({t('čeka izvještaj', 'awaiting report')})
                </span>
            );
        }
        const SB: Record<string, { bg: string; fg: string; text: string }> = {
            delivered: { bg: '#e4f3e9', fg: '#1f6e45', text: `✅ ${t('Uručeno', 'Delivered')}` },
            in_transit: { bg: '#fdf3e0', fg: 'var(--ad-acc-3)', text: `🚚 ${t('U dostavi', 'In transit')}` },
            returned: { bg: '#f7e3e3', fg: '#b14040', text: `↩ ${t('Vraćeno', 'Returned')}` },
            cancelled: { bg: '#eee', fg: '#777', text: `✖ ${t('Stornirano', 'Cancelled')}` },
        };
        const s = SB[link.status_norm] ?? { bg: '#eee', fg: '#777', text: link.status_norm };
        const subParts: string[] = [];
        if (link.barcode) subParts.push(String(link.barcode));
        if (link.delivery_hours != null) subParts.push(`${Math.round(Number(link.delivery_hours))}h`);
        if (link.recipient_place) subParts.push(String(link.recipient_place));
        const tip = [link.sent_at ? `${t('Poslano', 'Sent')}: ${link.sent_at}` : '', link.status_raw ? String(link.status_raw) : ''].filter(Boolean).join(' · ');
        return (
            <span onClick={(e) => { e.stopPropagation(); captureBarcode(o.order_number); }}
                title={tip || t('Klikni za ponovni unos barkoda', 'Click to re-enter barcode')}
                style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, cursor: 'pointer' }}>
                <span style={{ background: s.bg, color: s.fg, padding: '2px 8px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{s.text}</span>
                {subParts.length > 0 ? <span className="muted" style={{ fontSize: '0.68rem' }}>{subParts.join(' · ')}</span> : null}
            </span>
        );
    };

    // Zbrojevi za skup narudžbi: broj narudžbi, različitih artikala po narudžbi (zbrojeno), komada, finog zlata
    // i vrijednost u EUR (total_eur = iznos cijele narudžbe iz Faros snapshota).
    const orderTotals = (list: typeof eligible) => {
        let articles = 0, pcs = 0, fine = 0, eur = 0;
        for (const o of list) {
            const agg = aggregateOrderItems(o.items);
            articles += agg.length;
            for (const i of agg) { pcs += i.qty; fine += i.fine_gold; }
            eur += o.total_eur ?? 0;
        }
        return { orders: list.length, articles, pcs, fine, eur };
    };
    const fmtFine = (g: number) => g.toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const totalsText = (s: ReturnType<typeof orderTotals>) =>
        `${s.orders} ${t('narudžbi', 'orders')} · ${s.articles} ${t('artikala', 'articles')}${s.pcs !== s.articles ? ` (${s.pcs} ${t('kom', 'pcs')})` : ''} · ${fmtFine(s.fine)} g ${t('finog zlata', 'fine gold')} · ${fmtFine(s.eur)} €`;
    const tableColSpan = selectedWh.length > 1 ? 10 : 9;
    // Grupe u redoslijedu tijeka; unutar grupe ostaje odabrano sortiranje (sorted je već sortiran).
    const renderGroupedRows = () => {
        if (sorted.length === 0) return null;
        const groups = [...LIFECYCLE, 'none'].map((key) => ({
            key,
            rows: sorted.filter((o) => (currentLifecycleStatus(o) ?? 'none') === key),
        })).filter((g) => g.rows.length > 0);
        const grand = orderTotals(sorted);
        return (
            <>
                <tr style={{ background: 'rgba(var(--ad-acc-rgb),0.10)' }}>
                    <td colSpan={tableColSpan} style={{ fontSize: '0.8rem', fontWeight: 700, padding: '6px 10px' }}>
                        {t('Ukupno (filtrirano)', 'Total (filtered)')}: {totalsText(grand)}
                    </td>
                </tr>
                {groups.map((g) => {
                    const meta = g.key === 'none' ? { label: t('Bez statusa', 'No status'), color: '#6b7280' } : LIFECYCLE_LABEL[g.key];
                    const collapsed = collapsedGroups.has(g.key);
                    return (
                        <React.Fragment key={`grp-${g.key}`}>
                            <tr onClick={() => toggleGroup(g.key)} style={{ cursor: 'pointer', background: '#f7f5ee', userSelect: 'none' }}
                                title={collapsed ? t('Klikni za prikaz', 'Click to expand') : t('Klikni za sklapanje', 'Click to collapse')}>
                                <td colSpan={tableColSpan} style={{ padding: '6px 10px', borderTop: '1px solid rgba(var(--ad-soft-rgb),0.4)' }}>
                                    <span style={{ display: 'inline-block', width: 14, fontSize: '0.72rem' }}>{collapsed ? '▶' : '▼'}</span>
                                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: meta.color, background: `${meta.color}1a`, padding: '2px 8px', borderRadius: 10, marginRight: 8 }}>
                                        {meta.label}
                                    </span>
                                    <span style={{ fontSize: '0.78rem' }}>{totalsText(orderTotals(g.rows))}</span>
                                </td>
                            </tr>
                            {collapsed ? null : g.rows.map(renderOrderRow)}
                        </React.Fragment>
                    );
                })}
            </>
        );
    };

    // Redak liste narudžbi; isti render za ravni prikaz i za prikaz "Složi po statusu".
    const renderOrderRow = (o: typeof eligible[number]) => {
        const reserved = reservedOrders.has(`${(o as any).warehouse_code}|${o.order_number}`);
        const hasImamJu = (o.annotations ?? []).some((a: any) => a.annotation_type === 'imam_ju');
        const transferredAway = o.transferred_elsewhere === true;
        const transferredIn = o.transferred_in === true;
        const mjesto = (o.annotations ?? []).find((a: any) => a.annotation_type === 'mjesto_isporuke')?.value ?? null;
        const mjestoAnnId = (o.annotations ?? []).find((a: any) => a.annotation_type === 'mjesto_isporuke')?.id ?? null;
        // Prebačeno drugdje → siva/neaktivna kod mene (imam_ju ne vrijedi za moju poslovnicu).
        const rowHighlight: React.CSSProperties = transferredAway
            ? { opacity: 0.5, background: 'rgba(120,120,120,0.07)' }
            : transferredIn
                ? { background: 'rgba(31,122,54,0.10)', borderLeft: '3px solid #1f7a36' }
                : (reserved || hasImamJu) ? { background: 'rgba(31,122,54,0.06)' } : {};
        const marked = markedOrders.has(o.order_number);
        const rowStyle: React.CSSProperties = marked
            ? { ...rowHighlight, background: 'rgba(var(--ad-acc-rgb),0.22)', borderLeft: '3px solid var(--ad-acc)' }
            : rowHighlight;
        return (
            <tr key={`${(o as any).warehouse_code}|${o.order_number}`} onClick={() => setDrawer(o)} style={{ ...rowStyle, cursor: 'pointer' }} title={t('Klikni za detalje', 'Click for details')}>
                <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={marked} onChange={() => toggleMarked(o.order_number)}
                        title={t('Označi da šalješ ovu narudžbu (privremeno)', 'Mark that you are sending this order (temporary)')}
                        style={{ cursor: 'pointer', width: 16, height: 16 }} />
                </td>
                <td className="muted" style={{ fontSize: '0.74rem' }}>{o.item_date?.slice(0, 10) ?? '—'}</td>
                {selectedWh.length > 1 ? <td className="muted" style={{ fontSize: '0.74rem' }}>{(o as any).warehouse_name ?? (o as any).warehouse_code}</td> : null}
                <td style={{ fontSize: '0.78rem' }}>
                    {o.order_number}
                    {transferredAway ? <span title={t('Prebačeno na drugu poslovnicu', 'Transferred to another branch')} style={{ marginLeft: 5, fontSize: '0.7rem', color: '#888' }}>🔀 → {mjesto}</span> : null}
                    {transferredIn ? <span title={t('Prebačeno k tebi', 'Transferred to you') + (o.source_branch_name ? ` (${o.source_branch_name})` : '')} style={{ marginLeft: 5, background: '#1f7a36', color: '#fff', padding: '1px 6px', borderRadius: 10, fontSize: '0.66rem', fontWeight: 700 }}>📥 {t('PREBAČENO K TEBI', 'TRANSFERRED IN')}</span> : null}
                </td>
                <td><MaskedName name={o.partner_name} /></td>{/* Ime OSTAJE maskirano u prikazu; search radi po PUNOM imenu (backend šalje pravo ime za m.40.3). Vlasnik 2026-07-02. */}
                {/* Broj RAZLIČITIH proizvoda + ukupno komada (prije se prikazivao broj sirovih
                    Faros linija, pa je "5" značilo 5 linija, ne 5 proizvoda). */}
                {(() => {
                    const agg = aggregateOrderItems(o.items);
                    const qtySum = agg.reduce((s, i) => s + i.qty, 0);
                    return (
                        <>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} title={t(`${agg.length} različitih proizvoda, ukupno ${qtySum} kom`, `${agg.length} distinct products, ${qtySum} pcs total`)}>
                                {agg.length}{qtySum !== agg.length ? <span className="muted" style={{ fontSize: '0.72rem' }}> / {qtySum} kom</span> : null}
                            </td>
                            {/* P&G dopune / T87 (mail UID 663, 18.09.2026): status eksplicitno vidljiv u listi,
                                ne samo kroz boju retka. */}
                            <td style={{ whiteSpace: 'nowrap' }}>
                                {(() => {
                                    const st = currentLifecycleStatus(o);
                                    if (!st) return <span className="muted" style={{ fontSize: '0.72rem' }}>—</span>;
                                    const meta = LIFECYCLE_LABEL[st];
                                    return (
                                        <span title={orderStatusTooltip(st, locale)} style={{ fontSize: '0.7rem', fontWeight: 700, color: meta.color, background: `${meta.color}1a`, padding: '2px 7px', borderRadius: 10, cursor: 'help' }}>
                                            {meta.label}
                                        </span>
                                    );
                                })()}
                            </td>
                            {/* CIJELI popis (Danijela 22.07.2026.) — više se ne reže na 3 stavke. */}
                            <td style={{ fontSize: '0.74rem', maxWidth: 420, whiteSpace: 'normal', lineHeight: 1.35 }} className="muted"
                                title={orderItemsText(o.items)}>
                                {agg.map((i, ix) => (
                                    <span key={ix} style={{ whiteSpace: 'nowrap' }}>
                                        <strong style={{ color: 'var(--ad-ink)' }}>{i.qty}×</strong> {i.item_name}{ix < agg.length - 1 ? ' · ' : ''}
                                    </span>
                                ))}
                            </td>
                        </>
                    );
                })()}
                <td style={{ minWidth: 120 }} onClick={(e) => e.stopPropagation()}>
                    {renderParcelCell(o)}
                </td>
                <td style={{ minWidth: 260 }}>
                    {/* Dropdown s oznakama. Ako je prebačeno drugdje, "imam ju" ne vrijedi kod mene (siva). */}
                    {transferredAway ? (
                        <span className="muted" style={{ fontSize: '0.72rem', fontStyle: 'italic', display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            {t('Isporuka prebačena na', 'Delivery moved to')}: <strong>{mjesto}</strong>
                            {mjestoAnnId ? (
                                <button type="button" disabled={busy}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!confirm(t('Poništiti prebacivanje? Narudžba se vraća u normalu (oznaka mjesta isporuke se makne).', 'Undo transfer? Order returns to normal.'))) return;
                                        void (async () => {
                                            try { await mutate(`/offer-annotations/${mjestoAnnId}`, token, 'DELETE'); await load(); }
                                            catch (err) { alert(err instanceof Error ? err.message : 'API'); }
                                        })();
                                    }}
                                    title={t('Poništi prebacivanje — vrati narudžbu u normalu', 'Undo transfer — return order to normal')}
                                    style={{ background: '#fffdf6', color: 'var(--ad-acc-3)', border: '1.5px solid rgba(138,104,23,0.5)', borderRadius: 6, padding: '2px 9px', cursor: 'pointer', fontWeight: 700, fontSize: '0.7rem', fontStyle: 'normal' }}>
                                    ↩ {t('Poništi prebacivanje', 'Undo transfer')}
                                </button>
                            ) : null}
                        </span>
                    ) : (
                        <AnnotationStrip r={{ offer_document_id: o.offer_document_id, order_number: o.order_number, annotations: o.annotations ?? [] }} locale={locale} token={token} onChanged={() => void load()} readonly={false} isManager={isManager} />
                    )}
                </td>
            </tr>
        );
    };

    return (
        <div>
            <div style={{ background: 'rgba(31,122,54,0.06)', border: '1px solid rgba(31,122,54,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
                <strong style={{ fontSize: '0.86rem', color: '#1f7a36' }}>🔒 {t('Ručna alokacija narudžbi', 'Manual order allocation')}</strong>
                <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.78rem', lineHeight: 1.4 }}>
                    {t(
                        'Ako fizički već imaš robu za narudžbu (čeka kupca da dođe po njega) — klikni "Smješteno". Engine će tu narudžbu auto označiti kao L0 RUČNA ALOKACIJA i izuzeti robu iz free stock pool-a.',
                        'If you physically already have items for an order (waiting for customer) — click "Placed". Engine auto-marks order as L0 MANUAL ALLOCATION and excludes stock from free pool.',
                    )}
                </p>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', fontSize: '0.84rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span>{t('Poslovnice', 'Branches')}:</span>
                    <button type="button" onClick={() => setWhMenuOpen((o) => !o)}
                        style={{ padding: '4px 10px', cursor: 'pointer', border: '1px solid rgba(var(--ad-soft-rgb),0.5)', borderRadius: 6, background: '#fffdf6', fontSize: '0.82rem' }}>
                        {t('Poslovnice', 'Branches')} ({selectedWh.length}) ▾
                    </button>
                    {scope === 'all' ? <span title={t('Administrator — sve poslovnice', 'Administrator — all branches')} style={{ fontSize: '0.68rem', color: '#888' }}>({t('sve', 'all')})</span> : null}
                    {selectedWh.length > 0 ? (
                        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--ad-ink)', background: 'rgba(var(--ad-acc-rgb),0.12)', padding: '2px 9px', borderRadius: 6 }}
                            title={selectedWh.map((c) => allWh.find((w) => w.code === c)?.name ?? c).join(', ')}>
                            🏪 {selectedWh.length <= 3
                                ? selectedWh.map((c) => allWh.find((w) => w.code === c)?.name ?? c).join(' · ')
                                : `${selectedWh.length} ${t('poslovnica', 'branches')}`}
                        </span>
                    ) : null}
                    {whMenuOpen ? (
                        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 20, background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.5)', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', padding: 8, minWidth: 240, maxHeight: 320, overflowY: 'auto' }}>
                            <div style={{ display: 'flex', gap: 6, marginBottom: 6, borderBottom: '1px solid rgba(0,0,0,0.08)', paddingBottom: 6 }}>
                                <button type="button" onClick={() => setSelectedWh(allWh.map((w) => w.code))}
                                    style={{ flex: 1, padding: '3px 8px', cursor: 'pointer', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 5, background: '#f6fff8', fontSize: '0.74rem' }}>
                                    {t('Sve', 'All')}
                                </button>
                                <button type="button" onClick={() => setSelectedWh([])}
                                    style={{ flex: 1, padding: '3px 8px', cursor: 'pointer', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 5, background: '#fff6f6', fontSize: '0.74rem' }}>
                                    {t('Očisti', 'Clear')}
                                </button>
                            </div>
                            {allWh.map((w) => (
                                <label key={w.code} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '3px 2px', cursor: 'pointer', fontSize: '0.8rem' }}>
                                    <input type="checkbox" checked={selectedWh.includes(w.code)}
                                        onChange={() => setSelectedWh((cur) => cur.includes(w.code) ? cur.filter((c) => c !== w.code) : [...cur, w.code])}
                                        style={{ width: 15, height: 15, cursor: 'pointer' }} />
                                    <span>{w.code} · {w.name}</span>
                                </label>
                            ))}
                        </div>
                    ) : null}
                </div>
                <div style={{ position: 'relative', fontSize: '0.84rem', display: 'inline-flex', alignItems: 'center' }}>
                    <button type="button" onClick={() => setStatusMenuOpen((o) => !o)}
                        style={{ padding: '4px 10px', cursor: 'pointer', border: '1px solid rgba(var(--ad-soft-rgb),0.5)', borderRadius: 6, background: '#fffdf6', fontSize: '0.82rem' }}>
                        {t('Status', 'Status')} ({statusSel.length}) ▾
                    </button>
                    {statusMenuOpen ? (
                        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 20, background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.5)', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', padding: 8, minWidth: 220, maxHeight: 360, overflowY: 'auto' }}>
                            <div style={{ display: 'flex', gap: 6, marginBottom: 6, borderBottom: '1px solid rgba(0,0,0,0.08)', paddingBottom: 6 }}>
                                <button type="button" onClick={() => setStatusSel(ALL_STATUS_KEYS)}
                                    style={{ flex: 1, padding: '3px 8px', cursor: 'pointer', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 5, background: '#f6fff8', fontSize: '0.74rem' }}>
                                    {t('Sve', 'All')}
                                </button>
                                <button type="button" onClick={() => setStatusSel([])}
                                    style={{ flex: 1, padding: '3px 8px', cursor: 'pointer', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 5, background: '#fff6f6', fontSize: '0.74rem' }}>
                                    {t('Očisti', 'Clear')}
                                </button>
                            </div>
                            {STATUS_GROUPS.map((g) => (
                                <div key={g.header} style={{ marginBottom: 6 }}>
                                    <div className="muted" style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', margin: '4px 2px 2px' }}>{g.header}</div>
                                    {g.opts.map((opt) => (
                                        <label key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '3px 2px', cursor: 'pointer', fontSize: '0.8rem' }}>
                                            <input type="checkbox" checked={statusSel.includes(opt.key)}
                                                onChange={() => setStatusSel((cur) => cur.includes(opt.key) ? cur.filter((c) => c !== opt.key) : [...cur, opt.key])}
                                                style={{ width: 15, height: 15, cursor: 'pointer' }} />
                                            <span>{opt.label}</span>
                                        </label>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('🔍 Pretraga (broj, ime kupca, artikl)…', '🔍 Search (order, customer, item)…')}
                    style={{ flex: 1, minWidth: 240, padding: '5px 10px', fontSize: '0.84rem' }}
                />
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', cursor: 'pointer', userSelect: 'none' }}
                    title={t('Grupiraj narudžbe po statusu sa zbrojevima; unutar grupe vrijedi odabrano sortiranje', 'Group orders by status with totals; the selected sort applies within each group')}>
                    <input type="checkbox" checked={groupByStatus} onChange={(e) => setGroupByStatus(e.target.checked)} style={{ width: 15, height: 15, cursor: 'pointer' }} />
                    {t('Složi po statusu', 'Group by status')}
                </label>
                <button type="button" onClick={() => void exportXls()} disabled={sorted.length === 0} style={{ padding: '5px 10px', cursor: 'pointer', border: '1px solid rgba(var(--ad-soft-rgb),0.5)', borderRadius: 6, background: '#fffdf6', fontSize: '0.82rem' }}>📥 {t('Izvoz (xls)', 'Export (xls)')}</button>
                <span className="muted" style={{ fontSize: '0.78rem' }}>
                    {sorted.length} {t('narudžbi', 'orders')}
                    {reservedOrders.size > 0 ? ` · 🔒 ${reservedOrders.size} rezervirano` : ''}
                </span>
            </div>

            {err ? <p className="error">{err}</p> : null}
            {busy && eligible.length === 0 ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}
            {!busy && scope === 'assigned' && allWh.length === 0 ? (
                <p className="muted" style={{ fontStyle: 'italic' }}>{t('Nemaš dodijeljenih poslovnica. Obrati se administratoru (m.104).', 'No branches assigned to you. Contact your administrator (m.104).')}</p>
            ) : null}

            {selectedWh.length > 0 ? (
                <div className="table-wrap" style={{ maxHeight: 600, overflowY: 'auto', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 6 }}>
                    <table className="admin-user-table" style={{ fontSize: '0.82rem' }}>
                        <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                            <tr>
                                <th style={{ width: 28, textAlign: 'center' }} title={t('Označi (šaljem ovo)', 'Mark (sending this)')}>✓</th>
                                <th onClick={() => toggleSort('date')} style={{ cursor: 'pointer', userSelect: 'none' }}>{t('Datum', 'Date')}{sortArrow('date')}</th>
                                {selectedWh.length > 1 ? <th>{t('Poslovnica', 'Branch')}</th> : null}
                                <th onClick={() => toggleSort('order')} style={{ cursor: 'pointer', userSelect: 'none' }}>{t('Narudžba', 'Order')}{sortArrow('order')}</th>
                                <th onClick={() => toggleSort('partner')} style={{ cursor: 'pointer', userSelect: 'none' }}>{t('Kupac', 'Customer')}{sortArrow('partner')}</th>
                                <th onClick={() => toggleSort('items')} style={{ cursor: 'pointer', userSelect: 'none', textAlign: 'right' }} title={t('Broj različitih proizvoda / ukupno komada', 'Distinct products / total pieces')}>{t('Proizvoda', 'Products')}{sortArrow('items')}</th>
                                <th>{t('Status', 'Status')}</th>
                                <th>{t('Artikli (količina × proizvod)', 'Items (qty × product)')}</th>
                                <th>{t('Pošiljka', 'Parcel')}</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {groupByStatus ? renderGroupedRows() : sorted.map(renderOrderRow)}
                            {sorted.length === 0 && !busy ? (
                                <tr><td colSpan={selectedWh.length > 1 ? 9 : 8} className="muted" style={{ textAlign: 'center', padding: 20 }}>{t('Nema nedostavljenih narudžbi za ovu poslovnicu.', 'No undelivered orders for this branch.')}</td></tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="muted" style={{ fontStyle: 'italic' }}>{t('Odaberi barem jednu poslovnicu za prikaz narudžbi.', 'Pick at least one branch to see orders.')}</p>
            )}
            {drawer ? <OrderDrawer order={drawer} locale={locale} onClose={() => setDrawer(null)} /> : null}
        </div>
    );
}

/* ===== OrderDrawer — m.40.3 ručna alokacija narudžba detalj ===== */

/**
 * Zbroji stavke narudžbe PO PROIZVODU (Danijela 22.07.2026.: "molim da se dodaju ukupne količine
 * po proizvodu"). Faros šalje jednu liniju po komadu/partiji pa se isti artikl zna pojaviti više
 * puta — bez zbrajanja se "5 stavki" čita kao 5 različitih proizvoda.
 */
type OrderItem = { item_name: string; qty: number; fine_gold: number };

export function aggregateOrderItems(items: OrderItem[] | undefined | null): Array<OrderItem & { lines: number }> {
    const byName = new Map<string, OrderItem & { lines: number }>();
    for (const it of items ?? []) {
        const key = (it.item_name ?? '').trim();
        const cur = byName.get(key);
        if (cur) {
            cur.qty += Number(it.qty) || 0;
            cur.fine_gold += Number(it.fine_gold) || 0;
            cur.lines += 1;
        } else {
            byName.set(key, { item_name: key, qty: Number(it.qty) || 0, fine_gold: Number(it.fine_gold) || 0, lines: 1 });
        }
    }
    return Array.from(byName.values());
}

/** "2× Zlatnik 20 Kruna · 1× Bečki filharmoničar" — agregirano, za redak liste i XLS export. */
export function orderItemsText(items: OrderItem[] | undefined | null, maxNameLen?: number): string {
    return aggregateOrderItems(items)
        .map((i) => `${i.qty}× ${maxNameLen ? i.item_name.slice(0, maxNameLen) : i.item_name}`)
        .join(' · ');
}

type OrderForDrawer = {
    order_number: string;
    partner_name: string;
    item_date: string | null;
    items: Array<{ item_name: string; qty: number; fine_gold: number }>;
    offer_document_id?: number | null;
    transferred_elsewhere?: boolean;
    transferred_in?: boolean;
    source_branch_name?: string | null;
    annotations?: Array<{ id: number; annotation_type: string; set_at?: string; set_by_name?: string | null; agreed_delivery_date?: string | null; note?: string | null; value?: string | null }>;
};

function OrderDrawer({ order, locale, onClose }: { order: OrderForDrawer; locale: 'hr' | 'en'; onClose: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const annotations = order.annotations ?? [];

    // Detekcija statusa i carrier-a iz annotations
    const findAnn = (type: string) => annotations.find((a) => a.annotation_type === type);
    const STATUS_STEPS: Array<{ key: string; icon: string; label: string; color: string }> = ORDER_STATUS_SEQUENCE.map((k) => ({
        key: k, icon: ORDER_STATUS[k].icon, label: orderStatusLabel(k, locale), color: ORDER_STATUS[k].color,
    }));
    const reachedIdx = ORDER_STATUS_SEQUENCE.indexOf(currentOrderStatus(annotations));

    const carrierLabel: Record<string, string> = Object.fromEntries(['vozac', 'hp', 'gls', 'centralno', 'pohranjeno'].map((v) => [v, orderCarrierLabel(v, locale)]));
    const nacinIsporukeLabel: Record<string, string> = { u_poslovnici: t('U poslovnici', 'In branch'), vozac: t('Vozač', 'Driver'), hp: 'HP', gls: 'GLS' };

    const nacinIsporuke = findAnn('nacin_isporuke');
    const mjestoIsporuke = findAnn('mjesto_isporuke');
    const dogovorenDatum = findAnn('dogovoren_datum');
    const hitno = findAnn('hitno');
    const express = findAnn('express');
    const alarm = findAnn('alarm');

    // Non-status oznake (filter status iz "Sve oznake" sekcije)
    const STATUS_KEYS = new Set<string>(ORDER_STATUS_TYPES);
    const flagAnnotations = annotations.filter((a) => !STATUS_KEYS.has(a.annotation_type));

    const fmtDate = (s: string | undefined | null) => {
        if (!s) return '—';
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
        return m ? `${m[3]}.${m[2]}.${m[1]}.` : s;
    };
    const fmtDateTime = (s: string | undefined | null) => {
        if (!s) return '';
        const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(s);
        return m ? `${m[3]}.${m[2]}.${m[1]}. ${m[4]}:${m[5]}` : s;
    };

    const annotationMeta: Record<string, { icon: string; label: string; color: string }> = {
        hitno: { icon: '🚨', label: t('Hitno', 'Urgent'), color: '#b14040' },
        express: { icon: '🚀', label: 'Express', color: '#9333ea' },
        alarm: { icon: '⚠', label: 'ALARM', color: '#b14040' },
        dogovoren_datum: { icon: '📅', label: t('Dogovoren datum', 'Agreed date'), color: 'var(--ad-acc-3)' },
        nacin_isporuke: { icon: '🚚', label: t('Način isporuke', 'Delivery method'), color: '#1e40af' },
        mjesto_isporuke: { icon: '📍', label: t('Mjesto isporuke', 'Delivery place'), color: 'var(--ad-ink)' },
        napomena: { icon: '📝', label: t('Napomena', 'Note'), color: 'var(--ad-ink)' },
        // #9 Ružica/T87 (mail UID 663, 18.09.2026).
        na_cekanju_klijent: { icon: '⏸', label: t('Na čekanju', 'On hold'), color: '#b45309' },
    };

    const totalFineGold = order.items.reduce((s, it) => s + (it.fine_gold || 0), 0);
    // Prikaz PO PROIZVODU (zbrojene količine), a ne po sirovoj Faros liniji.
    const aggItems = aggregateOrderItems(order.items);
    const totalQty = aggItems.reduce((s, it) => s + it.qty, 0);

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 60, display: 'flex', justifyContent: 'flex-end' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(680px, 95vw)', background: '#fff', height: '100%', overflowY: 'auto', boxShadow: '-6px 0 20px rgba(0,0,0,0.15)' }}>
                {/* Sticky header */}
                <div style={{ position: 'sticky', top: 0, background: 'var(--ad-bg)', borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.3)', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, zIndex: 1 }}>
                    <strong style={{ color: 'var(--ad-ink)', fontSize: '1.08rem' }}>🧾 {order.order_number}</strong>
                    {hitno ? <span style={{ background: '#b14040', color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 800 }}>🚨 HITNO</span> : null}
                    {express ? <span style={{ background: '#9333ea', color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 800 }}>🚀 EXPRESS</span> : null}
                    {alarm ? <span style={{ background: '#b14040', color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 800 }}>⚠ ALARM</span> : null}
                    <button type="button" onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid #ccc', borderRadius: 5, padding: '4px 10px', cursor: 'pointer' }}>✕</button>
                </div>

                <div style={{ padding: 18 }}>
                    {/* Kupac + summary kartica */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                        <div style={{ padding: 10, background: 'var(--ad-bg)', borderRadius: 6 }}>
                            <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('Kupac', 'Customer')}</div>
                            <div style={{ fontWeight: 700, fontSize: '0.96rem', color: 'var(--ad-ink)', marginTop: 2 }}><MaskedName name={order.partner_name} /></div>
                        </div>
                        <div style={{ padding: 10, background: 'var(--ad-bg)', borderRadius: 6 }}>
                            <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('Datum / stavki', 'Date / items')}</div>
                            <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#444', marginTop: 2 }}>
                                {fmtDate(order.item_date)} · {order.items.length} {t('stavki', 'items')}
                                {totalFineGold > 0 ? <span style={{ marginLeft: 8, color: 'var(--ad-acc)', fontFamily: 'monospace' }}>· {totalFineGold.toFixed(2)} g Au</span> : null}
                            </div>
                        </div>
                    </div>

                    {/* Status banner */}
                    {order.transferred_in ? (
                        <div style={{ padding: 10, background: 'rgba(31,122,54,0.10)', border: '1px solid #1f7a36', borderRadius: 6, marginBottom: 10, color: '#1f7a36', fontWeight: 700 }}>
                            📥 {t('Prebačeno k tebi', 'Transferred in')}{order.source_branch_name ? ` ${t('iz', 'from')} ${order.source_branch_name}` : ''}
                        </div>
                    ) : null}
                    {order.transferred_elsewhere ? (
                        <div style={{ padding: 10, background: 'rgba(120,120,120,0.10)', border: '1px solid #888', borderRadius: 6, marginBottom: 10, color: '#666', fontWeight: 700 }}>
                            🔀 {t('Prebačeno na drugu poslovnicu', 'Transferred elsewhere')}{mjestoIsporuke?.value ? ` → ${mjestoIsporuke.value}` : ''}
                        </div>
                    ) : null}

                    {/* Status timeline */}
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: '0.74rem', color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{t('Status', 'Status')}</div>
                        <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
                            {STATUS_STEPS.map((step, idx) => {
                                // #11 Ružica/T87 (mail UID 663, 18.09.2026): "svi statusi bezbojni OSIM
                                // aktivnog, ostali bez boje" — prije je SVAKI dosegnuti korak dobivao
                                // punu step.color (npr. plavo za "Poslano"), pa je cijela traka do
                                // trenutnog statusa izgledala obojano. Sada boju nosi SAMO aktivni
                                // korak; prošli koraci su vidljivi (opacity 1) ali u neutralnoj sivoj,
                                // budući koraci ostaju izblijedjeli sivi kao i prije.
                                const isActive = idx === reachedIdx;
                                const isPast = idx < reachedIdx;
                                const ann = findAnn(step.key);
                                const whoWhen = ann ? `${t('Uneseno', 'Entered')}: ${ann.set_by_name ?? '—'}${ann.set_at ? ' · ' + fmtDateTime(ann.set_at) : ''}` : undefined;
                                const stepTitle = whoWhen ? `${whoWhen}\n\n${orderStatusTooltip(step.key, locale)}` : orderStatusTooltip(step.key, locale);
                                return (
                                    <React.Fragment key={step.key}>
                                        <div title={stepTitle} style={{ flex: 1, textAlign: 'center', opacity: isActive || isPast ? 1 : 0.35, cursor: 'help' }}>
                                            <div style={{
                                                width: 36, height: 36, borderRadius: '50%',
                                                background: isActive ? step.color : isPast ? '#9ca3af' : '#e5e5e5',
                                                color: isActive || isPast ? '#fff' : '#888',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: '1.1rem', margin: '0 auto', fontWeight: 700,
                                            }}>{step.icon}</div>
                                            <div style={{ marginTop: 4, fontSize: '0.72rem', fontWeight: 700, color: isActive ? step.color : '#aaa' }}>{step.label}</div>
                                            {ann?.set_at ? <div title={whoWhen} style={{ fontSize: '0.66rem', color: '#888', marginTop: 1, fontFamily: 'monospace', cursor: 'help' }}>{fmtDateTime(ann.set_at)}{ann.set_by_name ? ` · ${ann.set_by_name}` : ''}</div> : null}
                                            {(step.key === 'na_putu' || step.key === 'poslano') && ann?.value ? <div style={{ fontSize: '0.66rem', color: isActive ? step.color : '#888', marginTop: 1, fontWeight: 700 }}>{carrierLabel[ann.value] ?? ann.value}</div> : null}
                                        </div>
                                        {idx < STATUS_STEPS.length - 1 ? (
                                            <div style={{ flex: 0, alignSelf: 'center', marginTop: -10, color: '#ddd', fontSize: '1rem', padding: '0 2px' }}>→</div>
                                        ) : null}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>

                    {/* Isporuka info */}
                    <div style={{ marginBottom: 14, padding: 12, background: '#fafafa', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.2)' }}>
                        <div style={{ fontSize: '0.74rem', color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{t('Isporuka', 'Delivery')}</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: '0.84rem' }}>
                            <div>🚚 <strong>{t('Način', 'Method')}:</strong> <span style={{ color: nacinIsporuke ? '#1e40af' : '#aaa' }}>{nacinIsporuke?.value ? (nacinIsporukeLabel[nacinIsporuke.value] ?? nacinIsporuke.value) : '—'}</span></div>
                            <div>📍 <strong>{t('Mjesto', 'Place')}:</strong> <span style={{ color: mjestoIsporuke ? 'var(--ad-ink)' : '#aaa' }}>{mjestoIsporuke?.value ?? '—'}</span></div>
                            <div>📅 <strong>{t('Dogovoreni datum', 'Agreed date')}:</strong> <span style={{ color: dogovorenDatum ? 'var(--ad-acc-3)' : '#aaa', fontWeight: 700 }}>{dogovorenDatum?.agreed_delivery_date ? fmtDate(dogovorenDatum.agreed_delivery_date) : '—'}</span></div>
                            <div>🆔 <strong>offer:</strong> <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3, color: '#888' }}>{order.offer_document_id ?? '—'}</code></div>
                        </div>
                    </div>

                    {/* Stavke */}
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: '0.74rem', color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                            {t('Stavke', 'Items')} ({aggItems.length} {t('proizvoda', 'products')} · {totalQty} {t('kom', 'pcs')})
                        </div>
                        <div style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 6, overflow: 'hidden' }}>
                            <table style={{ width: '100%', fontSize: '0.84rem', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: 'var(--ad-bg)', color: 'var(--ad-ink)', fontSize: '0.74rem' }}>
                                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>{t('Artikl', 'Item')}</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', width: 70 }}>{t('Kol.', 'Qty')}</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', width: 90 }}>{t('Fino zlato', 'Fine gold')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {aggItems.map((it, i) => (
                                        <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                                            <td style={{ padding: '5px 8px' }}>
                                                {it.item_name}
                                                {it.lines > 1 ? (
                                                    <span className="muted" style={{ fontSize: '0.7rem', marginLeft: 6 }}
                                                        title={t(`Zbrojeno iz ${it.lines} linija narudžbe`, `Summed from ${it.lines} order lines`)}>({it.lines} {t('linije', 'lines')})</span>
                                                ) : null}
                                            </td>
                                            <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{it.qty}</td>
                                            <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'monospace', color: '#888' }}>{it.fine_gold ? `${it.fine_gold.toFixed(2)} g` : '—'}</td>
                                        </tr>
                                    ))}
                                    <tr style={{ borderTop: '2px solid var(--ad-acc)', background: 'var(--ad-bg)' }}>
                                        <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--ad-ink)' }}>{t('UKUPNO', 'TOTAL')}</td>
                                        <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: 'var(--ad-acc)' }}>{totalQty}</td>
                                        <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: 'var(--ad-acc)' }}>{totalFineGold > 0 ? `${totalFineGold.toFixed(2)} g` : '—'}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Sve oznake (osim status) */}
                    {flagAnnotations.length > 0 ? (
                        <div style={{ marginBottom: 14 }}>
                            <div style={{ fontSize: '0.74rem', color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{t('Sve oznake', 'All flags')} ({flagAnnotations.length})</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {flagAnnotations.map((a) => {
                                    const meta = annotationMeta[a.annotation_type] ?? { icon: '🏷', label: a.annotation_type, color: '#888' };
                                    return (
                                        <div key={a.id} style={{ padding: 8, background: '#fff', border: `1px solid ${meta.color}`, borderLeft: `4px solid ${meta.color}`, borderRadius: 4, fontSize: '0.84rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <span style={{ fontSize: '1rem' }}>{meta.icon}</span>
                                                <strong style={{ color: meta.color }}>{meta.label}</strong>
                                                {a.value ? <span style={{ color: '#444' }}>: <code style={{ background: '#fafafa', padding: '1px 5px', borderRadius: 3 }}>{a.value}</code></span> : null}
                                                {a.agreed_delivery_date ? <span style={{ color: 'var(--ad-acc-3)' }}>📅 {fmtDate(a.agreed_delivery_date)}</span> : null}
                                                <span style={{ marginLeft: 'auto', color: '#888', fontSize: '0.72rem', fontFamily: 'monospace' }}>{fmtDateTime(a.set_at)}</span>
                                            </div>
                                            {a.note ? <div style={{ marginTop: 4, color: '#555', fontStyle: 'italic', fontSize: '0.8rem' }}>„{a.note}"</div> : null}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
